import { z } from 'zod';
/**
 * Env se validuje pri startu a padne okamzite (fail-fast), ne az pri prvnim requestu.
 * Zadny process.env pristup nikde jinde v kodu - vzdy pres tento modul.
 */
export const envSchema = z
    .object({
    NODE_ENV: z.enum(['development', 'test', 'production']).default('development'),
    /**
     * Nasazeni se stejnym `NODE_ENV=production`, ale bez zivych externich bran
     * (SMS, platby...) - kazda vetev, ktera se rozhoduje podle `NODE_ENV`, se
     * musi rozhodovat podle tohoto priznaku, pokud se ma na preview chovat
     * jinak nez v produkci (ES3 bod B.1). Vychozi je `production` (fail-safe:
     * kdyz se zapomene nastavit, chova se jako ostra produkce).
     */
    REOS_DEPLOYMENT: z.enum(['preview', 'production']).default('production'),
    /**
     * Krok DEMO7 — VEREJNE DEMO BEZ HESLA (`demo.maklerov.cz`).
     *
     * `1` = tenhle PROCES obsluhuje samostatnou demo instanci: vlastni DB, ve
     * ktere jsou JEN ukazkova data, prihlaseni jednim klikem a nic odchoziho.
     * Je to priznak HOSTITELE, ne kancelare — zakaz podle slugu `demo`
     * (`isDemoTenant`, krok D1) plati dal a je prvni obrannou linii; tenhle
     * priznak pridava druhou: na verejnem hostu nesmi byt ZIVA brana ani
     * dostupna cesta k registraci, platbe nebo prevzeti uctu, i kdyby se slug
     * kancelare jednou zmenil.
     *
     * FAIL-CLOSED ma dve urovne:
     *  * START: `publicDemoEnvViolations` neprovoz s zivou branou vubec
     *    nenastartuje (viz `superRefine` nize),
     *  * BEH: `publicDemoGuardDecision` (`apps/api/src/lib/public-demo-guard.ts`)
     *    pousti na `/api/auth/*` jen vyctene cesty a demo ucet.
     *
     * Vychozi `0`: zapomenuta promenna NIKDY neudela z ostre instance demo.
     */
    REOS_PUBLIC_DEMO: z.enum(['0', '1']).default('0'),
    /**
     * E-mail demo uctu, kterym `/demo` prihlasuje. Na verejnem demu je to
     * JEDINY pripustny login (kdokoli jiny by si pres `/api/auth/*` sahnul na
     * ukazkovou kancelar vlastnim uctem). Prazdne pri `REOS_PUBLIC_DEMO=1`
     * = chyba pri startu, protoze brana by nemela co porovnavat.
     */
    REOS_PUBLIC_DEMO_EMAIL: z.string().email().optional(),
    /**
     * Krok DEMO11 bod 1 — CESTA K SOUBORU S HESLEM DEMO UCTU (ne heslo samo).
     * Relaci dema spousti SERVER (`POST /v1/public/demo/session`), takze heslo
     * uz necestuje do prohlizece ani nelezi ve statickem HTML stranky `/demo`.
     * Zdroj pravdy zustava JEDEN soubor, ktery na server zapisuje clovek
     * (`${DEMO_ROOT}/demo-account.pw`, chmod 600) — proto cesta, a ne druha
     * kopie hodnoty v `.env` (CLAUDE.md pravidlo 11 a §8 bod 2).
     * Nenastaveno = cesta `/v1/public/demo/session` odpovi 503 a stranka
     * posle navstevnika na `/login`.
     */
    REOS_PUBLIC_DEMO_PASSWORD_FILE: z.string().optional(),
    LOG_LEVEL: z.enum(['fatal', 'error', 'warn', 'info', 'debug', 'trace']).default('info'),
    API_PORT: z.coerce.number().int().positive().default(3000),
    /** Rate limit per tenant/uzivatel/IP (krok 11), pozadavku za minutu. */
    RATE_LIMIT_MAX: z.coerce.number().int().positive().default(300),
    /** Prometheus endpoint workera (krok 9); 0 = vypnout HTTP server metrik. */
    WORKER_METRICS_PORT: z.coerce.number().int().min(0).default(9101),
    /**
     * Recovery scan workflow timeru (krok 10, 06 §5 "denni scan" fallback, kdyby
     * Redis ztratil delayed joby); ms, default 24h.
     */
    WORKFLOW_TIMER_SCAN_MS: z.coerce.number().int().min(0).default(86_400_000),
    DATABASE_URL: z.string().min(1),
    DATABASE_SYSTEM_URL: z.string().min(1).optional(),
    REDIS_URL: z.string().min(1).optional(),
    DEFAULT_LOCALE: z.string().default('cs-CZ'),
    DEFAULT_COUNTRY: z.string().length(2).default('CZ'),
    DEFAULT_CURRENCY: z.string().length(3).default('CZK'),
    DEFAULT_TIMEZONE: z.string().default('Europe/Prague'),
    OTEL_EXPORTER_OTLP_ENDPOINT: z.string().optional(),
    OTEL_SERVICE_NAME: z.string().default('reos-api'),
    /**
     * Krok 7 (identity): nazev produktu a domeny NIKDY nezapisujeme natvrdo
     * (brief krok 7 - blokujici vstup). REOS_ISSUER_URL = verejna URL API
     * (OIDC issuer + zaklad pro cookie domenu a WebAuthn rpID).
     */
    REOS_ISSUER_URL: z.string().url().optional(),
    /**
     * Krok R1.5 (identity): verejna URL web-admin SPA - zaklad pro odkazy v
     * e-mailech (reset hesla, pozvanky) a pro trustedOrigins Better Auth
     * (frontend bezi na jinem originu nez API). Nazev domeny NIKDY natvrdo.
     */
    WEB_ADMIN_URL: z.string().url().optional(),
    /**
     * Krok QAFIX8 bod 2 (V9): VEREJNA adresa aplikace pro odkazy v e-mailech
     * uzivatelum adminu (pozvanka, reset hesla, overeni e-mailu, magic link).
     *
     * `WEB_ADMIN_URL` je na preview chranena basic auth — odkaz na ni je pro
     * pozvaneho makiere slepa ulicka (prohlizec se ho zepta na jmeno a heslo,
     * ktere nema). Tahle promenna je proto zaklad VSECH odkazu v poste; pro
     * `trustedOrigins` zustava `WEB_ADMIN_URL` v platnosti dal (odkud se smi
     * prihlasit se tim neomezuje).
     */
    PUBLIC_APP_URL: z.string().url().optional(),
    /**
     * FÁZE P4 (portál): další originy pro Better Auth `trustedOrigins`, čárkou
     * oddělené (klientské domény `<domena>/admin` + záložní `<slug>.admin.redhand.cz`).
     * Bez toho originCheck middleware odmítne přihlášení z domény, kterou
     * `WEB_ADMIN_URL`/`REOS_ISSUER_URL` nepokrývají. Prázdné/nenastavené = beze změny.
     */
    REOS_TRUSTED_ORIGINS: z.string().optional(),
    /** Podpisovy klic Better Auth. V production POVINNY (hlida @reos/identity). */
    AUTH_SECRET: z.string().min(16).optional(),
    /**
     * KEK (Key Encryption Key) pro envelope sifrovani citlivych poli
     * (`@reos/security`, krok 15). V production POVINNY (hlida party kontext);
     * v dev/test staci testovaci hodnota z .env.example. Format: 32B base64/
     * base64url/hex, jina hodnota = passphrase stretch (dokumentovano v modulu).
     */
    REOS_KEK: z.string().min(16).optional(),
    /**
     * Retence CRM stran (krok 15): interval behu jobu a rezim dry-run.
     * Dry-run je DEFAULT - anonymizace zapina explicitne konfiguraci (vzor z
     * Pobytniku: retention-worker nikdy nemaze napoprve).
     */
    PARTY_RETENTION_INTERVAL_MS: z.coerce.number().int().min(0).default(86_400_000),
    PARTY_RETENTION_DRY_RUN: z
        .enum(['true', 'false'])
        .default('true')
        .transform((value) => value === 'true'),
    /**
     * Krok CRM2: interval doplňování automatických úkolů makléře
     * (`crm_tasks` z otevřených bran auditu, položek „K doplnění“, termínů
     * smluv, neuhrazených faktur a předpisů a poptávek bez kontaktu či
     * nabídky). 0 = vypnuto, běží jen ruční volání.
     *
     * Čtvrt hodiny, protože běh je idempotentní (každý úkol má klíč
     * `<druh>:<id zdroje>` a duplicitní běh nic nezaloží) a zároveň
     * dostatečně rychlý, aby „termín smlouvy do týdne" a „poptávka bez
     * kontaktu 24 h" nečekaly na maklera.
     */
    CRM_AUTO_TASK_INTERVAL_MS: z.coerce.number().int().min(0).default(900_000),
    /**
     * Krok NAP4 bod A10: interval dávkového přepočtu embeddingů (popisy
     * nabídek a texty poptávek kanceláře). Běh je INKREMENTÁLNÍ — nezměněný
     * záznam model nevolá (`content_hash` v `ai_embeddings`) — takže kratší
     * interval neznamená počítání znovu. **0 = vypnuto** (výchozí stav:
     * přepočet bez dostupného embedding providera nemá co dělat).
     */
    AI_EMBEDDING_REFRESH_INTERVAL_MS: z.coerce.number().int().min(0).default(0),
    /**
     * Krok AS1: interval stahování schránek makléřů (jen čtení, IMAP EXAMINE
     * + BODY.PEEK). Tři minuty dle zadání; běh je idempotentní (UID +
     * UIDVALIDITY, Message-ID), kratší interval nic nezdvojí. 0 = vypnuto.
     */
    BROKER_INBOX_POLL_INTERVAL_MS: z.coerce.number().int().min(0).default(180_000),
    /**
     * Krok MRGS1 bod 3: hosty, na které schránka makléře smí jít, i když vedou
     * do vnitřní sítě. Čárkou. **V produkci prázdné** — makléřem zadaný IMAP
     * server se po DNS rozlišení odmítne, pokud míří na loopback, privátní
     * rozsah, link-local, CGNAT, IPv6 ULA nebo na adresu serveru REOS
     * (`publicHostGuard` v `@reos/communication`). Naplňuje se jen pro dev
     * a živé ověření (GreenMail na 127.0.0.1).
     */
    BROKER_INBOX_ALLOWED_HOSTS: z.string().default(''),
    /**
     * Krok P1EXP2 bod 1: interval stahování reakcí z API portálů (Sreality
     * `listFullInquiry`, jen čtení). Běh je idempotentní (deduplikace ohlasů
     * v CRM podle e-mailu/telefonu + inzerátu), takže kratší interval nic
     * nezdvojí. 0 = vypnuto. POZOR: samotný interval nestačí — běh je navíc
     * za příznakem `SREALITY_INQUIRIES=1` (reakce vyžadují smlouvu se
     * Sreality), takže bez příznaku se na síť nejde vůbec.
     */
    PORTAL_INQUIRY_POLL_INTERVAL_MS: z.coerce.number().int().min(0).default(300_000),
    /**
     * Krok P1EXP3 bod 1: interval stahování NÁVŠTĚVNOSTI z API portálů
     * (Sreality `listAllDailyStat`, jen čtení). Import je idempotentní —
     * druhý zápis téhož dne hodnotu přepíše, takže kratší interval nic
     * nesečte. Vychozí 6 h: portál dává denní řadu, častější dotaz by jen
     * spotřeboval limity. 0 = vypnuto (zůstává ruční CSV import).
     */
    PORTAL_STATS_POLL_INTERVAL_MS: z.coerce.number().int().min(0).default(21_600_000),
    /**
     * Krok P1EXP3 bod 3: interval plánovače reportů majiteli. Perioda u
     * zakázky je dny (`owner_report_settings.frequency_days`), takže sweep
     * stačí hodinový — co není na řadě, se přeskočí. 0 = vypnuto.
     * ODESLÁNÍ NA SKUTEČNOU ADRESU MAJITELE je navíc za příznakem
     * `OWNER_REPORT_LIVE_SEND=1`; bez něj jde report na testovací adresu.
     */
    REOS_OWNER_REPORT_INTERVAL_MS: z.coerce.number().int().min(0).default(3_600_000),
    /**
     * Krok NAP2 bod A4: interval plánovače stažení inzerátu po uplynutí
     * „ponechat jako prodáno N dní“ (`listing_drafts.sold_keep_until`).
     * Volba je ve dnech, takže hodinový sweep stačí. 0 = vypnuto (stažení
     * zůstává jen na ruční akci makléře). Nic neposílá ven sám: stažení jde
     * TOUTÉŽ hromadnou akcí jako ruční potvrzení, tedy přes režimovou bránu —
     * při vypnutém `portal_live_export` skončí v TEST transportu.
     */
    REOS_SOLD_KEEP_SWEEP_INTERVAL_MS: z.coerce.number().int().min(0).default(3_600_000),
    /**
     * Krok CEN2: interval sweepu „stáhni inzeráty kanceláře po konci
     * zkušebního období bez platby" (lhůta 14 dní, `trialLapsePlan`).
     * Lhůta je ve dnech, takže hodinový sweep by stačil — ale VÝCHOZÍ 0 =
     * VYPNUTO a zapíná člověk: tohle stahuje inzeráty ZÁKAZNÍKA z portálů,
     * což je nevratné vůči portálu, a nesmí to naskočit jen tím, že se
     * nasadí nová verze. Stejný postoj jako u připomínek trialu
     * (`COMMERCE_TRIAL_NOTICE_INTERVAL_MS`). Mazání dat tím NEZAPÍNÁ —
     * `scheduledDataDeletions` je jen výpis a mazat neumí.
     */
    REOS_TRIAL_LAPSE_SWEEP_INTERVAL_MS: z.coerce.number().int().min(0).default(0),
    /**
     * Krok NAP15 bod A8: interval plánovače hlídání ceny (`price-watch.ts`
     * konečně s volajícím). Ceny nabídek v okolí se mění v řádu dnů, takže
     * denní sweep stačí; VÝCHOZÍ 0 = VYPNUTO, zapíná člověk — upozornění
     * makléře vzniká ve frontě `crm_notifications` a první běh by jich nad
     * celou kanceláří vyrobil hodně najednou.
     */
    REOS_PRICE_WATCH_INTERVAL_MS: z.coerce.number().int().min(0).default(0),
    /** Krok NAP15 bod A8: práh odchylky naší ceny od mediánu okolí v % (ostrá hranice). */
    REOS_PRICE_WATCH_THRESHOLD_PCT: z.coerce.number().min(1).max(100).default(10),
    /**
     * Krok DOM1: interval plánovače vlastních domén kanceláří (ověření DNS,
     * vystavení/obnova certifikátu, zapnutí směrování). VÝCHOZÍ 0 =
     * VYPNUTO, zapíná ops po nasazení nginx šablony — běh bez připravené
     * konfigurace by vystavoval certifikáty pro domény, které nemá kdo
     * obsloužit. Hodina stačí: DNS u registrátora se propaguje v desítkách
     * minut a certifikát se obnovuje ve dnech.
     */
    REOS_CUSTOM_DOMAIN_INTERVAL_MS: z.coerce.number().int().min(0).default(0),
    /** Krok DOM1: soubor s aktivními doménami, který čte mapa v nginx. */
    REOS_CUSTOM_DOMAIN_MAP_FILE: z.string().default('/etc/nginx/reos-custom-domains.map'),
    /** Krok DOM1: ops skript, který vystaví certifikát a přeloží nginx. */
    REOS_CUSTOM_DOMAIN_CERT_SCRIPT: z.string().default('/opt/reos/ops/custom-domains/issue-cert.sh'),
    /** Krok P1EXP3 bod 3: ostré odesílání reportů majitelům (zapíná člověk). */
    OWNER_REPORT_LIVE_SEND: z.string().optional(),
    /** Krok P1EXP3 bod 3: kam chodí reporty, dokud ostré odesílání není zapnuté. */
    OWNER_REPORT_TEST_RECIPIENT: z.string().email().optional(),
    /**
     * Krok SR3b bod 2: tajemství pro PODPIS odhlašovacího odkazu v reportu
     * majiteli (HMAC-SHA256 nad `case_id`, stejný vzor jako
     * `COMMERCE_DELIVERY_LINK_SECRET` u odkazu na report objednávky).
     *
     * SECRET — patří do SOPS, nikdy do repa. Bez něj se odhlašovací odkaz
     * do reportu NEVLOŽÍ a žádný podpis neplatí (fail-closed); odhlásit
     * majitele pak může makléř v nastavení u zakázky.
     */
    OWNER_REPORT_UNSUBSCRIBE_SECRET: z.string().min(16).optional(),
    /**
     * Krok CRM3: interval odesílání e-mailových připomínek před událostí
     * v kalendáři makléře. 0 = vypnuto, běží jen ruční tlačítko „Odeslat
     * připomínky teď" (které je jen pro vedoucího, stejně jako spuštění
     * generátoru úkolů).
     *
     * Patnáct minut z téhož důvodu jako u úkolů: připomínka má mřížku
     * 15 min / 1 h / 24 h a běh je idempotentní (`reminder_sent_at is
     * null`), takže kratší interval neznamená duplicity. Zároveň je 15 min
     * dost, aby „připomínka hodinu předtím" nepřišla sice po termínu.
     */
    CRM_CALENDAR_REMINDER_INTERVAL_MS: z.coerce.number().int().min(0).default(900_000),
    /**
     * Krok CRM4: interval sweepu „připomínka prohlídky den předem" v
     * apps/worker (0 = vypnuto). Sweep jen ZAŘADÍ e-mail do fronty
     * (`crm_outbound_mails` + outbox) a rezervaci označí
     * (`reminder_queued_at`), takže kratší interval neznamená duplicity.
     */
    CRM_VIEWING_REMINDER_INTERVAL_MS: z.coerce.number().int().min(0).default(900_000),
    /**
     * Krok NOTIF1 bod 4: interval souhrnu nepřečtených upozornění makléři
     * (0 = vypnuto). Hodina je ta „jednou za hodinu" ze zadání; pracovní
     * dobu a vypnutí si řídí každá kancelář sama
     * (`crm_notification_settings`), ne tenhle interval. Běh je
     * idempotentní (`digested_at is null`), takže kratší interval
     * neznamená druhý e-mail o tomtéž.
     */
    CRM_NOTIFICATION_DIGEST_INTERVAL_MS: z.coerce.number().int().min(0).default(3_600_000),
    /**
     * Krok NOTIF1 bod 5: interval sweepu stavu úhrad z MyÚčta (0 =
     * vypnuto, běží jen ruční tlačítko na dokladu). Dvě hodiny ze zadání:
     * MyÚčto páruje bankovní výpisy samo a REOS jen čte výsledek, takže
     * častější dotazování by jen přidalo volání bez nové informace.
     */
    REOS_PAYMENT_SYNC_INTERVAL_MS: z.coerce.number().int().min(0).default(7_200_000),
    /**
     * Krok MRGC1 (nalez N4 reportu CRM4): LHUTA SMAZANI odeslanych e-mailu
     * klientovi. `crm_outbound_mails` drzi cele telo e-mailu, tedy jmeno,
     * adresu a termin zajemce, ktery casto ani klientem kancelare neni.
     *
     * ROZHODNUTI MRGC1-RETENCE: 90 dni od odeslani. Dukaz „co komu odeslo"
     * zustava navzdy v `audit_events` (`crm.mail_queued`, `crm.mail_sent`:
     * druh, adresat, predmet), takze telo je potreba jen na provozni spor
     * („pozvanku jsem nedostal") — na to je kvartal dost. Fronta ceka jen na
     * odeslani, ne na archivaci (GDPR minimalizace, 05 §9).
     */
    CRM_OUTBOUND_MAIL_RETENTION_DAYS: z.coerce.number().int().min(1).default(90),
    /**
     * Krok MRGC1: interval uklidu fronty e-mailu v apps/worker (0 = vypnuto).
     * DRY-RUN je DEFAULT jako u ostatnich retenci — ostry beh se zapina
     * vedome (`CRM_OUTBOUND_MAIL_RETENTION_DRY_RUN=false`).
     */
    CRM_OUTBOUND_MAIL_RETENTION_INTERVAL_MS: z.coerce.number().int().min(0).default(86_400_000),
    CRM_OUTBOUND_MAIL_RETENTION_DRY_RUN: z
        .enum(['true', 'false'])
        .default('true')
        .transform((value) => value === 'true'),
    /**
     * Krok CER1 bod 4: uklid chyb z prohlizece (`client_error_events`).
     * Lhuta je 90 dni a NENI v env — je to nase diagnostika, ne nastaveni
     * kancelare (`CLIENT_ERROR_RETENTION_DAYS` v `@reos/db`). V env je jen
     * interval behu (0 = vypnuto).
     *
     * OSTRY BEH JE TU DEFAULT, na rozdil od retenci osobnich udaju: mazou se
     * jen nase technicke zaznamy o chybach, ne data kancelare — a dry-run by
     * znamenal, ze tabulka roste navzdy a nikdo si toho nevsimne.
     */
    CLIENT_ERROR_RETENTION_INTERVAL_MS: z.coerce.number().int().min(0).default(86_400_000),
    CLIENT_ERROR_RETENTION_DRY_RUN: z
        .enum(['true', 'false'])
        .default('false')
        .transform((value) => value === 'true'),
    /**
     * Krok 16 (jurisdiction CZ): live volani na narodni adresni registr RUUIAN.
     * DEFAULT je offline fixture (zadna sit v testech/CI); live=true povoluji
     * jen prostredi s egress politiku (connector-runner vzor, threat model §4).
     */
    RUIAN_LIVE: z
        .enum(['true', 'false'])
        .default('false')
        .transform((value) => value === 'true'),
    /**
     * Krok W4: vychozi je skutecna sluzba CUZK ArcGIS REST (RUIAN, vrstva
     * AdresniMisto) - overeno 26. 9. 2026 zivym dotazem. Bez teto hodnoty by
     * `RUIAN_LIVE=true` bez `RUIAN_API_URL` nemel na co volat.
     */
    RUIAN_API_URL: z
        .string()
        .url()
        .default('https://ags.cuzk.gov.cz/arcgis/rest/services/RUIAN/MapServer'),
    /**
     * Krok KN1 (API dat katastru nemovitostí, ČÚZK REST): live volani na
     * `api-kn.cuzk.gov.cz`. Stejny vzor jako `RUIAN_LIVE` - DEFAULT offline
     * fixtura, v testech a CI se na CUZK NIKDY nevola. Limit sluzby je
     * 500 volani/den na klic, proto nejde o zdroj pro hromadne prohledavani
     * (ROZHODNUTI R.B. 12. 10. 2026: jen zakazka a reporty).
     */
    KN_LIVE: z
        .enum(['true', 'false'])
        .default('false')
        .transform((value) => value === 'true'),
    KN_API_URL: z.string().url().default('https://api-kn.cuzk.gov.cz/api/v1'),
    /**
     * Krok KN1b — podminky API KN ČÚZK (bod 10) zakazuji predat klic „jine
     * osobe nebo sluzbe". Vlastni klic KANCELARE by to porusil, dokud ČÚZK
     * nepotvrdi opak. Vychozi FALSE: funkce z KN1 zustava v kodu, ale je
     * vypnuta, dokud R.B. flag nezapne (ROZHODNUTI R.B. 12. 10. 2026).
     */
    KN_OFFICE_KEY_ENABLED: z
        .enum(['true', 'false'])
        .default('false')
        .transform((value) => value === 'true'),
    /**
     * Krok KN4 — SIRENI UDAJU KATASTRU. § 53 zakona o katastru: udaje
     * katastru se smi sirit jen se souhlasem ČÚZK (§ 57 pokuta do 100 000 Kč),
     * a plati to i pro neosobni data z API KN. Dokud souhlas nemame, NESMI
     * zadny vystup (PDF podrobne i klientske, HTML nahledy, interni audit,
     * panel parcely, verejny web) nest fakta z KN — ani z API KN, ani
     * z Nahlizeni, ani z dalkoveho pristupu, ani z rucniho opisu LV.
     *
     * Vychozi FALSE. Kod z KN1 zustava, jen mlci; prepnuti na `true` je
     * jediny zasah, ktery po odpovedi ČÚZK bude potreba.
     * SSOT pravidla „co je udaj z KN": `@reos/duediligence` →
     * `public/kn-output-policy.ts`.
     */
    KN_OUTPUTS_ENABLED: z
        .enum(['true', 'false'])
        .default('false')
        .transform((value) => value === 'true'),
    /**
     * Platformni klic Maklerova - pouzije se, kdyz kancelar vlastni klic
     * nevlozila. Secret, nikdy do logu ani do odpovedi API.
     */
    REOS_KN_APIKEY: z.string().min(10).optional(),
    /**
     * Krok GCAL1: prihlasovaci udaje UCTU SLUZBY pro Google Calendar API -
     * bud CESTA k JSON souboru (vzor hradyprodeti.cz), nebo JSON rovnou.
     * Secret: nikdy do logu ani do odpovedi API (ven jde jen `client_email`,
     * aby s nim makler mohl svuj kalendar nasdilet). Bez teto promenne je
     * propojeni s Google kalendarem VYPNUTE a UI to rekne.
     */
    GOOGLE_CALENDAR_CREDENTIALS: z.string().min(1).optional(),
    /**
     * Denni strop volani na PLATFORMNI klic pro JEDNU kancelar, aby jedna
     * kancelar nevycerpala 500/den vsem ostatnim. Vlastni klic kancelare
     * tenhle strop nema - tam plati jen limit CUZK.
     */
    KN_PLATFORM_DAILY_LIMIT_PER_TENANT: z.coerce.number().int().min(0).default(100),
    /**
     * Krok 17 (documents/storage): S3-kompatibilni object storage (ADR-08).
     * `S3_ENDPOINT` = sitova cesta serveru/kontejneru, `S3_PUBLIC_ENDPOINT` =
     * adresa dosazitelna KLIENTEM (presigned URL se generuji z ni). Kredence
     * a buckety jsou per prostredi; bez endpointu storage operace failnou az
     * pri pouziti (lazy), aby API slo postavit i bez S3 v test env.
     */
    S3_ENDPOINT: z.string().url().optional(),
    S3_PUBLIC_ENDPOINT: z.string().url().optional(),
    S3_REGION: z.string().min(1).default('us-east-1'),
    S3_ACCESS_KEY: z.string().min(3).optional(),
    S3_SECRET_KEY: z.string().min(3).optional(),
    /** Legacy/dev bucket pro tridu files; produkce ma explicitni tri. */
    S3_BUCKET: z.string().min(1).optional(),
    S3_BUCKET_FILES: z.string().min(1).optional(),
    S3_BUCKET_MEDIA: z.string().min(1).optional(),
    S3_BUCKET_ARCHIVE: z.string().min(1).optional(),
    /**
     * ClamAV clamd TCP host - kdyz je nastaven, scan bezi pres INSTREAM.
     * Bez nej (dev/test) pada DevScanner (magic bytes + EICAR); produkcni
     * start BEZ CLAMD_HOST je zamitnut wiringem workera.
     */
    CLAMD_HOST: z.string().min(1).optional(),
    CLAMD_PORT: z.coerce.number().int().positive().default(3310),
    /** Presigned TTL: PUT 5 min, GET cap 15 min (akceptace kroku 17). */
    DOCUMENTS_PRESIGN_PUT_TTL_S: z.coerce.number().int().min(30).max(300).default(300),
    DOCUMENTS_PRESIGN_GET_TTL_S: z.coerce.number().int().min(60).max(900).default(900),
    /**
     * Krok O6 (media nemovitosti). `MEDIA_DIRECT_S3=1` = fotky si prohlizec
     * tahne presigned GETem primo z S3 (produkce s VEREJNYM S3 endpointem).
     * DEFAULT 0 = cteni jde streamem pres API - na preview je S3_PUBLIC_ENDPOINT
     * interni adresa a presigned URL by z prohlizece nedosahla (ROZHODNUTI O6).
     */
    MEDIA_DIRECT_S3: z
        .enum(['0', '1'])
        .default('0')
        .transform((value) => value === '1'),
    /** Limit jedne fotky pri uploadu pres API (multipart), krok O6. */
    MEDIA_MAX_UPLOAD_BYTES: z.coerce
        .number()
        .int()
        .min(65_536)
        .default(15 * 1024 * 1024),
    /** Kolik souboru smi projit jednim multipart uploadem (krok O6). */
    MEDIA_MAX_FILES_PER_UPLOAD: z.coerce.number().int().min(1).max(100).default(30),
    /** Delsi hrana nahledu v px; nahledy generuje worker do webp (krok O6). */
    MEDIA_THUMB_MAX_PX: z.coerce.number().int().min(64).max(2048).default(480),
    /** Retence dokumentu: interval behu jobu + rezim (dry-run je DEFAULT). */
    DOCUMENT_RETENTION_INTERVAL_MS: z.coerce.number().int().min(0).default(86_400_000),
    DOCUMENT_RETENTION_DRY_RUN: z
        .enum(['true', 'false'])
        .default('true')
        .transform((value) => value === 'true'),
    /** SMTP pro magic link / OTP / pozvanky; fallback SMTP_HOST+SMTP_PORT. */
    SMTP_URL: z.string().min(1).optional(),
    /**
     * Krok GEN1: zaklad REST API ARES pro overeni IČO pri registraci kancelare
     * (`AresIcoLookup`). Bez hodnoty verejny ARES; nastavuje se jen pro
     * generalku/e2e na lokalni atrapu, aby beh nesahal na skutecny registr.
     */
    ARES_BASE_URL: z.string().url().optional(),
    SMTP_HOST: z.string().min(1).optional(),
    SMTP_PORT: z.coerce.number().int().positive().optional(),
    /** Odesilatel systemovych e-mailu (reset hesla, pozvanky), napr. 'REOS <no-reply@redhand.cz>'. Bez nej 'no-reply@localhost' = spam; default jen pro dev/Mailpit. */
    SMTP_FROM: z.string().min(3).optional(),
    /**
     * Krok MAIL2 — OSTRA POSTA JEN NA POVOLENE ADRESY.
     *
     * Seznam CELYCH adres nebo DOMEN ve tvaru `@redhand.cz` (carkou). Vzor je
     * `SMS_ALLOWED_RECIPIENTS` (krok P6A) a duvod je tyz: od chvile, kdy
     * `SMTP_URL` miri na skutecny relay, znamena kazda testovaci zprava
     * e-mail skutecnemu cloveku — klientovi z importu, preklepem zapsane
     * adrese, zakaznikovi jine kancelare. R.B. chce poštu zkousenou naostro,
     * ale jen na nasi domene.
     */
    MAIL_ALLOWED_RECIPIENTS: z.string().default(''),
    /**
     * Krok MAIL2 — `allowlist` (VYCHOZI, fail-closed) posle pres ostry relay
     * jen zpravu, jejiz VSICHNI prijemci jsou v `MAIL_ALLOWED_RECIPIENTS`;
     * prazdny seznam tedy znamena, ze ostre neodejde nic. `open` je chovani
     * do MAIL2 (posli komukoli) a patri produkci, kde uz pilot skoncil —
     * nikdy se na nej nespadne mlcenim: vychozi hodnota je ta bezpecna.
     */
    MAIL_RECIPIENT_POLICY: z.enum(['allowlist', 'open']).default('allowlist'),
    /**
     * Krok MAIL2 — ZACHYTAVACI SMTP, kam jde kopie zpravy, kterou politika
     * nepustila ven (dnes mailpit). Bez hodnoty se pouzije mistni mailpit
     * (`localhost:1025`), takze zapomenuta promenna NEznamena ztracenou
     * zpravu — jen ji nenajdeme nikde jinde nez v mistni schrance.
     *
     * Musi to byt MISTNI schranka (`isDevMailSinkTarget`); relay do internetu
     * tady je pokyn „posli to stejne ven", tedy presne obejdeni pojistky.
     */
    MAIL_CATCH_URL: z.string().min(1).optional(),
    /**
     * Krok 21 (communication): Resend email gateway + SMS/WhatsApp stub.
     * Credentials NIKDY natvrdo - jen z prostredi (CLAUDE.md pravidlo 6).
     */
    RESEND_API_KEY: z.string().min(1).optional(),
    RESEND_WEBHOOK_SECRET: z.string().min(1).optional(),
    /**
     * Krok SMS1: `android-gateway` = telefon se SIM kanceláře přes aplikaci
     * SMS Gateway for Android (sms-gate.app) - místo placené brány. `twilio`
     * zůstává jen jako rezervovaná hodnota výčtu (nikdy neimplementováno -
     * SMS1 ji nenahrazuje, jen vedle ní staví reálného providera). Stub je
     * výchozí - android-gateway se zapíná jen explicitní konfigurací.
     */
    SMS_PROVIDER: z.enum(['stub', 'twilio', 'android-gateway']).default('stub'),
    SMS_API_KEY: z.string().min(1).optional(),
    /** cloud = veřejný relay api.sms-gate.app; local = telefon v síti (např. přes tunel). */
    SMS_GATEWAY_MODE: z.enum(['cloud', 'local']).default('cloud'),
    /**
     * Cloud mode má veřejný výchozí relay; local mode ho MUSÍ dostat explicitně
     * (adresa telefonu/tunelu je jiná pro každou instalaci - nikdy se neodhaduje).
     */
    SMS_GATEWAY_URL: z.string().url().default('https://api.sms-gate.app/3rdparty/v1'),
    /** Přihlašovací údaje z aplikace na telefonu (Cloud sekce / Local Server). NIKDY natvrdo. */
    SMS_GATEWAY_USER: z.string().min(1).optional(),
    SMS_GATEWAY_PASSWORD: z.string().min(1).optional(),
    /** Denní strop odeslaných SMS (ochrana SIM před chováním, které operátor vidí jako spam). */
    SMS_GATEWAY_DAILY_LIMIT: z.coerce.number().int().positive().default(200),
    /**
     * Tajný token v URL webhooku doručenek (sent/delivered/failed) - aplikace
     * na telefonu webhooky nepodepisuje (stejné ROZHODNUTÍ jako Brevo, KAMP3).
     */
    SMS_GATEWAY_WEBHOOK_SECRET: z.string().min(16).optional(),
    /**
     * Krok E2E5 bod 1 — ČÍSLA, KTERÁ NIKDY NESMÍ JÍT DO ŽIVÉ BRÁNY.
     *
     * Seznam předvoleb (čárkou) v E.164. SMS na číslo s některou z nich jde
     * VŽDY do stubu, bez ohledu na `SMS_PROVIDER` — tedy i na preview, kde
     * `android-gateway` posílá přes skutečný telefon. Výchozí `+420999` je
     * rozsah, který v českém číslovacím plánu nepatří žádnému mobilnímu
     * operátorovi, takže e2e sady jím mohou generovat čísla bez rizika, že
     * zprávu dostane cizí člověk (6. 10. 2026 takhle odešel kód pro podpis na
     * +420777175759 — viz REPORT-step-PIL4.md §Nálezy).
     *
     * Prázdná hodnota = žádné přesměrování (jediný legitimní případ je test
     * proti MOCKU brány, `playwright.sms1.config.ts`).
     */
    SMS_STUB_RECIPIENT_PREFIXES: z.string().default('+420999'),
    /**
     * Krok P6A — SKUTECNA SMS JEN NA CISLA, KTERA MAJITEL SIM POVOLIL.
     *
     * Seznam CELYCH cisel v E.164 (carkou). `SMS_STUB_RECIPIENT_PREFIXES` resi
     * jen TESTOVACI rozsah; pilot P6 (podpis obou stran) potrebuje skutecnou
     * SMS, ale smi ji dostat pouze ucastnik pilotu. Cokoli jineho — preklep
     * v telefonu strany, cizi cislo z importu, zakaznik jine kancelare —
     * znamena SMS z telefonu majitele SIM nekomu, kdo o ni nepozadal.
     *
     * Zapis je tolerantni (mezery, `00420`): hodnotu pise clovek do `.env`
     * a normalizuje se stejne jako cislo prichozi z aplikace, aby „+420 777 …"
     * a „00420777…" znamenaly tyz telefon.
     */
    SMS_ALLOWED_RECIPIENTS: z.string().default(''),
    /**
     * Krok P6A — `allowlist` (VYCHOZI, fail-closed) posle do zive brany jen
     * cisla z `SMS_ALLOWED_RECIPIENTS`; prazdny seznam tedy znamena, ze
     * neodejde nic. `open` je dnesni chovani (posle komukoli) a je urcene pro
     * testy proti MOCKU brany a pro dobu, kdy pilot skonci — nikdy se na nej
     * nespadne mlcenim: vychozi hodnota je ta bezpecna.
     */
    SMS_RECIPIENT_POLICY: z.enum(['allowlist', 'open']).default('allowlist'),
    /**
     * Krok AKV1: akvizicni radar (samostatna sluzba, partnerske API). Bez URL
     * a tokenu je sekce Akvizice v adminu v sedem stavu „Připravujeme" a API
     * vraci 503 — nikdy tichy mock. Token je jeden pro cele REOS (kancelar
     * urcuje hlavicka `X-REOS-Office-Id` ze session); NIKDY do logu.
     */
    AKV_RADAR_URL: z.string().url().optional(),
    AKV_REOS_SERVICE_TOKEN: z.string().min(16).optional(),
    /** Jak dlouho cekat na radar, nez ho admin ukaze jako nedostupny. */
    AKV_RADAR_TIMEOUT_MS: z.coerce.number().int().positive().max(60_000).default(8_000),
    /**
     * Krok UXV4c (nabor na miste): prepis reci na text. `TRANSCRIBE_URL` =
     * interni sluzba s OpenAI-kompatibilnim `POST /v1/audio/transcriptions`
     * (navrh: speaches/faster-whisper `large-v3` na Ryzenu). Bez ni se
     * prepisuje jen klicem kancelare (KR1), jinak sedy stav „Připravujeme".
     */
    TRANSCRIBE_URL: z.string().url().optional(),
    TRANSCRIBE_MODEL: z.string().min(1).default('large-v3'),
    TRANSCRIBE_TIMEOUT_MS: z.coerce.number().int().positive().max(3_600_000).default(600_000),
    /**
     * Krok NAP5 bod A11 (hlas pro Asistentku): `POST /v1/assistant/transcribe`
     * jde na rh-whisper TYMZE klientem jako diktat v naboru (`HttpTranscriber`,
     * OpenAI-kompatibilni `POST /v1/audio/transcriptions`). ROZHODNUTI NAP5:
     * kdyz `ASSISTANT_WHISPER_URL` chybi, padne se zpatky na `TRANSCRIBE_URL` —
     * je to tataz sluzba a kancelar nema dve whisper instance. Bez obou je
     * mikrofon v okne Asistentky skryty a routa vraci 503 (nikdy tichy mock).
     */
    ASSISTANT_WHISPER_URL: z.string().url().optional(),
    /** Krok UXV4c bod 8: po kolika dnech se nahravka naboru smaze (prepis zustava). */
    FIELD_INTAKE_RECORDING_RETENTION_DAYS: z.coerce.number().int().min(1).max(365).default(30),
    /** Jak casto worker maze nahravky po lhute (0 = vypnuto; lhuta je slib majiteli). */
    FIELD_INTAKE_RETENTION_INTERVAL_MS: z.coerce.number().int().min(0).default(3_600_000),
    WHATSAPP_PROVIDER: z.enum(['stub', 'meta']).default('stub'),
    WHATSAPP_API_KEY: z.string().min(1).optional(),
    COMMUNICATION_FROM_DOMAIN: z.string().min(3).optional(),
    /**
     * Krok MOB1 bod 3 — „Nahlásit problém" posílá SERVER na RedHand Podporu
     * (ingest `POST {PODPORA_API_URL}/api/v1/tickets`). Token NIKDY nesmí do
     * prohlížeče; bez obou hodnot administrace tlačítko vůbec neukáže
     * (`flags.supportReporting` v `GET /api/me`) a routa vrací 503.
     */
    PODPORA_API_URL: z.string().url().optional(),
    PODPORA_TOKEN_REOS: z.string().min(16).optional(),
    /** Krok 25 (shared identity): OIDC provider JWKS perzistence (RS256). */
    REOS_OIDC_JWKS_JSON: z.string().optional(),
    /**
     * BLOK 9 (audit bez chatu): fronta audit_runs ve workeru. Soubeh = kolik
     * behu smi worker zpracovavat najednou (vychozi 1, viz brief Ukol 2).
     */
    AUDIT_QUEUE_CONCURRENCY: z.coerce.number().int().min(1).default(1),
    /** Polling interval claim smycky (FOR UPDATE SKIP LOCKED nad audit_runs). */
    AUDIT_QUEUE_POLL_MS: z.coerce.number().int().min(200).default(3_000),
    /**
     * Model pro headless `claude -p` beh + eskalacni retez pri vycerpani
     * limitu (vzor /root/rh-runner.sh: MODELS_DEFAULT). Prvni model je default,
     * dalsi se zkousi jen pri detekci "limit" ve vystupu.
     */
    AUDIT_AGENT_MODELS: z.string().default('claude-sonnet-5,claude-opus-5,claude-fable-5'),
    /** Strop kroku agenta v jednom pokusu (claude -p --max-turns). */
    AUDIT_AGENT_MAX_TURNS: z.coerce.number().int().min(1).default(800),
    /** Cekani pred retry po transientni chybe (ECONNRESET/529/502 apod.), ms. */
    AUDIT_AGENT_TRANSIENT_RETRY_MS: z.coerce.number().int().min(0).default(180_000),
    /** Strop pokusu pres vsechny modely dohromady, nez beh spadne do FAILED. */
    AUDIT_AGENT_MAX_ATTEMPTS: z.coerce.number().int().min(1).default(14),
    /**
     * Krok K6 — TVRDY STROP JEDNOHO BEHU AUDITU (ms, vychozi 45 min).
     *
     * Doklad, proc to je: beh `01a0e2f1-6cfd-…` visel ve stavu RUNNING tri
     * hodiny, protoze `audit_runs.timeout_ms` (90 min ze schematu) se nedal
     * zvednout z konfigurace a proces zruseni signalu neposlouchal. Bere se
     * PRISNEJSI z obou hodnot — zakazka smi chtit kratsi beh, delsi ne.
     */
    AUDIT_RUN_HARD_LIMIT_MS: z.coerce.number().int().min(60_000).default(2_700_000),
    /**
     * Krok K7 — VOLITELNY STROP PODLE DRUHU NEMOVITOSTI (ms).
     *
     * Zmereno nad zivou preview DB 28. 9. 2026 (`audit_runs` dokoncene behy,
     * viz REPORT-step-K7.md): uspesne behy FLAT az 2075 s, LAND az 1889 s,
     * HOUSE az 1489 s — spolecny strop 45 min (2 700 000 ms) ma u vsech druhu
     * rezervu. Bez nastaveni plati `AUDIT_RUN_HARD_LIMIT_MS` pro vsechny druhy
     * stejne (dnesni chovani); promenna dovoluje strop pozdeji zpritsnit nebo
     * uvolnit jen pro jeden druh, az bude dost mereni pro konkretni druh zvlast.
     */
    AUDIT_RUN_HARD_LIMIT_MS_FLAT: z.coerce.number().int().min(60_000).optional(),
    AUDIT_RUN_HARD_LIMIT_MS_HOUSE: z.coerce.number().int().min(60_000).optional(),
    AUDIT_RUN_HARD_LIMIT_MS_LAND: z.coerce.number().int().min(60_000).optional(),
    /**
     * Krok K6: po jake dobe BEZ POSTUPU (ne bez tepu) se beh povazuje za
     * zaseknuty — poplach do Telegramu a ukonceni procesu. Vychozich 20 min.
     */
    AUDIT_RUN_STALL_MS: z.coerce.number().int().min(60_000).default(1_200_000),
    /**
     * Krok K6: kolikrat se beh po preruseni (timeout / zaseknuti) automaticky
     * zopakuje. Vychozi 1; `0` opakovani vypne. Chyba modelu se tim neopakuje —
     * tu resi retry uvnitr `agent-runner.ts`.
     */
    AUDIT_RUN_TIMEOUT_RETRIES: z.coerce.number().int().min(0).max(3).default(1),
    /**
     * Krok ZDARMA1 (R.B. 14. 10. 2026) — STROP BEZPLATNYCH ZAKLADNICH BEHU
     * na kancelar a KALENDARNI MESIC. Zakladni proverení je zdarma, takze
     * jedine, co ho chrani proti zneuziti, je pocet; cislo je proto
     * konfigurovatelne v configu (vychozi 30 podle zadani) a ne zapecene
     * v routě. `0` = zadny bezplatny beh (strop plati hned od prvniho).
     *
     * Vyjimku NEMA .env, ale ZARAZENI KANCELARE: demo kancelar skutecny beh
     * vubec nezaradi (DEMO7c) a interni testovaci kancelar
     * (`tenants.is_internal_test`) se do stropu nepocita — jinak by vlastni
     * QA stropem zavrelo cestu, kterou ma merit.
     */
    AUDIT_BASIC_RUNS_PER_MONTH: z.coerce.number().int().min(0).default(30),
    /**
     * Krok K6: kam hlidac posila poplach. TYZ bot jako `ops/deploy.sh` a Uptime
     * Kuma — druhy kanal by znamenal druhe misto, kam se nikdo nediva. Bez obou
     * promennych se poplach jen zaloguje jako `error` (provoz se nemeni).
     * Token je SECRET a patri do SOPS (`ops/secrets/README.md`), ne do repa.
     */
    TELEGRAM_BOT_TOKEN: z.string().min(1).optional(),
    TELEGRAM_CHAT_ID: z.string().min(1).optional(),
    /**
     * Krok O5b (brandovane exporty): Gotenberg — HTTP sluzba, ktera prevadi
     * HTML na PDF (kontejner `reos-gotenberg`). Bez teto promenne fronta
     * exportu ve workeru NEBEZI (a `POST …/export/*.pdf` skonci ve stavu
     * QUEUED) — chromium binarka se do image API ani workera zamerne nedava.
     */
    GOTENBERG_URL: z.string().url().optional(),
    /** Polling interval claim smycky nad dd_exports. */
    EXPORT_QUEUE_POLL_MS: z.coerce.number().int().min(200).default(2_000),
    /** Kolik exportu smi worker prevadet soubezne. */
    EXPORT_QUEUE_CONCURRENCY: z.coerce.number().int().min(1).default(2),
    /**
     * Krok K1b: po dobehu auditu (DONE/NEEDS_REVIEW) worker sam zaradi PDF
     * export pro tyhle druhy (carkou oddeleny seznam `ReportKind` z
     * @reos/reports - validace hodnot je az na strane workera, config balicek
     * @reos/reports importovat nesmi, viz eslint.config.js boundaries).
     * Vychozi `interni-audit` + `technicky-report` (K0c) - klientsky report je
     * to, co makler(ka) po dobehu skutecne posila dal, takze cekat na rucni
     * kliknuti nema smysl. `prezentace-majiteli` se auto negeneruje (ROZHODNUTI
     * K1b, dokument pro majitele si maklerka posle rucne, az bude hotovy
     * k prezentaci).
     */
    AUDIT_AUTO_EXPORT_KINDS: z.string().default('interni-audit,technicky-report'),
    /**
     * Krok A3: kterym enginem bezi ostry audit.
     *
     * `legacy` (VYCHOZI) = jeden headless `claude -p` na PREDPLATNE identite
     * (ROZHODNUTI K0b). `roles` = pipeline roli pres router A1 (deterministika ->
     * lokal -> API klic firmy, viz `docs/platform/AGENT-SYSTEM.md`).
     *
     * Vychozi hodnota se NEMENI, dokud srovnani na zlate sade (E1,
     * `ops/eval-engines.sh`) neukaze paritu — prepnuti je rozhodnuti cloveka
     * (CLAUDE.md §1 bod 10). Neznama hodnota = chyba startu, ne tichy legacy.
     */
    AUDIT_ENGINE: z.enum(['legacy', 'roles']).default('legacy'),
    /**
     * Krok A3: strop nakladu na JEDNU zakazku napric rolemi, v Kc. Router A1 hlida
     * rozpocet jedne role (profil), tohle je soucet za beh. Prekroceni znamena
     * NEEDS_REVIEW, ne tiche pokracovani (zadani A3 bod 3).
     *
     * Vychozich 40 Kc je STROP, ne ocekavani: AGENT-SYSTEM §8 odhaduje konfiguraci
     * D (jen Analytik na API) vyrazne pod 21 Kc na report.
     */
    AUDIT_ROLES_BUDGET_CZK: z.coerce.number().min(0).default(40),
    /**
     * Krok KB1 (R24, R.B. 7. 10. 2026): engine REPORTU uvnitř `AUDIT_ENGINE=roles`.
     *
     * `rules` (VYCHOZI) = Analytik a Redaktor bezi pres pravidlovy engine
     * (rozpor pozna uz deterministicky kolektor, status CONFLICT) — 0 Kc, 0
     * volani modelu. `cloud` = dnesni cesta pres router A1, jen pro srovnani
     * na zlate sade; v produkci vypnuto.
     */
    REPORT_ENGINE: z.enum(['rules', 'cloud']).default('rules'),
    /**
     * Krok A2: brana vydani reportu (Oponent + Kontrola konzistence, @reos/ai).
     *
     * `off` (VYCHOZI) = role se nespusti a auto-export jde jako dosud. `block`
     * = report s nalezem (nebo report, u ktereho se role nepodarilo spustit)
     * se NEVYDA a ceka na cloveka — fail-closed.
     *
     * Treti stav „zaloguj a pust" tu zamerne NENI: brana, ktera nic neblokuje,
     * je dekorace a po prvnim falesnem poplachu ji stejne nikdo necte
     * (CLAUDE.md §1 bod 7). Zapnuti vyzaduje bezici lokalni model (`OLLAMA_URL`);
     * bez nej brana v rezimu `block` zastavi kazdy export.
     */
    AI_RELEASE_GATE: z.enum(['off', 'block']).default('off'),
    /**
     * Krok K0b (naklad behu): kurz USD -> CZK, kterym se prepocitava
     * `total_cost_usd` z `claude -p --output-format json` na `cost_czk`.
     * Numeric STRING (Money pravidlo - nikdy float), ne `z.coerce.number()`.
     * Default je provozni odhad, ne kurz CNB - proto se vedle nej VZDY uklada
     * i `fx_rate_source`, aby bylo videt, odkud cislo je.
     */
    FX_USD_CZK: z
        .string()
        .regex(/^\d+(\.\d{1,6})?$/)
        .default('23.500000'),
    /** Odkud kurz pochazi (jde do `dd_research_log.fx_rate_source`). */
    FX_RATE_SOURCE: z.string().min(1).default('config:FX_USD_CZK'),
    /**
     * Krok K0b: interval zdravotni kontroly registru zdroju (GetCapabilities
     * VERIFIED zdroju). 0 = vypnuto; default 24 h = "1x denne" ze zadani.
     */
    SOURCE_HEALTH_INTERVAL_MS: z.coerce.number().int().min(0).default(86_400_000),
    /**
     * Krok D2b — BRANA UPLNOSTI REPORTU: jak casto se zkusi dotahnout zdroje,
     * ktere zadrzely report objednany zakaznikem, a dodat ho sam. 0 = sweep
     * vypnuty (report pak ceka, dokud zdroje nedotahne dalsi beh prověření).
     * Vychozich 30 min: kazdy tik znovu spusti kolektor nad zakazkou, takze
     * kratsi interval znamena vic dotazu na cizi uredni zdroje.
     */
    REPORT_DELIVERY_SWEEP_INTERVAL_MS: z.coerce.number().int().min(0).default(1_800_000),
    /**
     * Krok D2b — jak dlouho smi report cekat na vypadly zdroj, nez to pujde do
     * interni fronty RedHand (Dnesek admina). Zadani R.B. 7. 10. 2026: vychozi
     * 6 h. Objednavka pritom ZUSTAVA ve stavu „zpracováváme" — zakaznik
     * neuplny report nedostane ani po limitu, jen se o tom dozvi clovek.
     */
    REPORT_DELIVERY_WAIT_LIMIT_MS: z.coerce.number().int().min(60_000).default(21_600_000),
    /** Krok O6: polling claim smycky nad `property_media` (fronta `media-thumb`). */
    MEDIA_QUEUE_POLL_MS: z.coerce.number().int().min(200).default(2_000),
    /** Kolik nahledu smi worker generovat soubezne. */
    MEDIA_QUEUE_CONCURRENCY: z.coerce.number().int().min(1).default(2),
    /** Krok UI1b: polling claim smycky nad `listing_videos` (fronta videi z fotek). */
    LISTING_VIDEO_QUEUE_POLL_MS: z.coerce.number().int().min(200).default(5_000),
    /**
     * Kolik videi smi worker renderovat soubezne. Vychozi 1 — jedno video je
     * desitky sekund CPU na ffmpegu (Ken Burns prepocitava kazdy snimek)
     * a dve soubezne by na stejnem stroji brzdily API.
     */
    LISTING_VIDEO_QUEUE_CONCURRENCY: z.coerce.number().int().min(1).default(1),
    /**
     * Krok NAP7 bod A17: polling claim smycky nad
     * `contract_counterparty_extractions` (fronta vytezeni smlouvy protistrany).
     */
    CONTRACT_EXTRACT_QUEUE_POLL_MS: z.coerce.number().int().min(200).default(5_000),
    /**
     * Kolik smluv protistrany smi worker vytezovat soubezne. Vychozi 1 — kazde
     * vytezeni je OCR celeho PDF + lokalni model, tedy desitky sekund CPU.
     */
    CONTRACT_EXTRACT_QUEUE_CONCURRENCY: z.coerce.number().int().min(1).default(1),
    /** Krok K2: polling claim smycky nad `dd_maps` (fronta mapovych vyrezu). */
    MAP_QUEUE_POLL_MS: z.coerce.number().int().min(200).default(2_000),
    /**
     * Kolik vyrezu smi worker renderovat soubezne. Vychozi 1 - kazdy render je
     * nekolik dotazu na cizi WMS a mapove sluzby CUZK nejsou nas testovaci stroj.
     */
    MAP_QUEUE_CONCURRENCY: z.coerce.number().int().min(1).default(1),
    /**
     * Krok NAP16 (A31) — 3D MODEL DOMU (Blender) V MAPOVE FRONTE.
     *
     * Jadro (`buildHouseSceneSpec`, `renderHouseScene`, krok R3D1/AI6) je
     * v repu od 7. 10. 2026 a nemelo volajiciho. Tenhle flag ho zapina:
     * kolektor zaradi vrstvu `house_3d`, worker ji vyrenderuje headless
     * Blenderem a PNG + GLB ulozi do S3.
     *
     * VYCHOZI FALSE a je to zamerne: Blender na serveru 138 nainstalovany
     * NENI (zive 20. 10. 2026) a render je ~90 s CPU na jednu scenu. Dokud
     * se nenainstaluje a nenastavi `HOUSE_3D_BLENDER_BIN`, nesmi se zakazka
     * zdrzovat na uloze, ktera stejne spadne.
     *
     * GPT finale zustava vypnute i pri zapnutem flagu (ROZHODNUTI R.B.
     * 7. 10. 2026 „Blender-first", viz `house-3d-gate.ts`).
     */
    HOUSE_3D_ENABLED: z
        .enum(['true', 'false'])
        .default('false')
        .transform((value) => value === 'true'),
    /** Binarka headless Blenderu; bez ni se render nespousti ani pri zapnutem flagu. */
    HOUSE_3D_BLENDER_BIN: z.string().min(1).optional(),
    /**
     * Cesta ke skriptu sceny (`internal/maps/blender/house_scene.py`). Je to
     * python pro Blender, ne TypeScript — do `dist` se nepreklada, takze ho
     * prostredi musi ukazat cestou. Bez ni render nebezi.
     */
    HOUSE_3D_BLENDER_SCRIPT: z.string().min(1).optional(),
    /**
     * `nice` priorita renderu — render je CPU-heavy a nesmi predbihat zakazky
     * maklere (tataz uvaha jako `MAP_QUEUE_CONCURRENCY` = 1).
     */
    HOUSE_3D_NICE_LEVEL: z.coerce.number().int().min(0).max(19).default(10),
    /**
     * Krok K10: auto-export `technicky-report` pred vykreslenim pocka nejvyse
     * tuhle dobu, nez fronta vyrezu (`MAP_QUEUE_CONCURRENCY`) dobehne - report
     * se pak nevykresli bez mapy, ktera jeste muze vzniknout (nalez
     * `evals/results/2026-09-28-1715-regression.md`). Vychozi 45 s pokryje
     * beznou dobu renderu i mensi zdrzeni ve fronte; kdyz limit vyprsi,
     * report se vykresli bez mapy jako dosud (K8 bod 1 - neutralni text).
     */
    MAP_RENDER_WAIT_TIMEOUT_MS: z.coerce.number().int().min(0).default(45_000),
    /**
     * Krok K3b: schranka, do ktere chodi odpovedi spravcu siti. Bez `HOST`
     * worker prijmu nebezi vubec - nespousti se "na slepo" proti localhostu.
     * Jedna schranka = JEDEN tenant (`UTILITY_INBOX_TENANT_ID`): o tom, komu
     * posta patri, nesmi rozhodovat jeji obsah.
     */
    UTILITY_INBOX_IMAP_HOST: z.string().min(1).optional(),
    UTILITY_INBOX_IMAP_PORT: z.coerce.number().int().min(1).max(65_535).default(993),
    UTILITY_INBOX_IMAP_SECURE: z.coerce.boolean().default(true),
    UTILITY_INBOX_IMAP_USER: z.string().min(1).optional(),
    UTILITY_INBOX_IMAP_PASSWORD: z.string().min(1).optional(),
    UTILITY_INBOX_IMAP_MAILBOX: z.string().min(1).default('INBOX'),
    UTILITY_INBOX_TENANT_ID: z.string().uuid().optional(),
    /** Servisni ucet, jehoz jmenem se prijem zapisuje do auditu. */
    UTILITY_INBOX_USER_ID: z.string().uuid().optional(),
    UTILITY_INBOX_POLL_MS: z.coerce.number().int().min(1_000).default(60_000),
    UTILITY_INBOX_BATCH: z.coerce.number().int().min(1).max(100).default(20),
    /**
     * Krok A4 (Korespondent): identita, jmenem ktere odchazi dotazy spravcum
     * siti, SVJ a obcim, a schranka, do ktere chodi odpovedi.
     *
     * Bez `CORRESPONDENCE_MAILBOX_DOMAIN` a `CORRESPONDENCE_REQUESTER_NAME`
     * se navrhy dotazu VUBEC NESKLADAJI - dopis „od neurcene kancelare"
     * s odpovedni adresou na localhostu by byl horsi nez zadny dopis.
     *
     * ODESLANI TIM NEZAPINA. Kazdy jeden e-mail uvolnuje clovek ve fronte
     * „dotazy ke schvaleni"; tyhle promenne rikaji jen, JAK bude dopis vypadat
     * a kam ma adresat odpovedet.
     */
    CORRESPONDENCE_MAILBOX_DOMAIN: z.string().min(3).optional(),
    /** Lokalni cast odpovedni schranky (`vyjadreni+<token>@domena`). */
    CORRESPONDENCE_MAILBOX_LOCALPART: z.string().min(1).default('vyjadreni'),
    /** `false` = schranka plus-adresy neumi; token pujde jen do predmetu. */
    CORRESPONDENCE_MAILBOX_PLUS: z.coerce.boolean().default(true),
    /** Odesilatel (From). Kdyz chybi, pouzije se odpovedni adresa schranky. */
    CORRESPONDENCE_FROM_ADDRESS: z.string().min(3).optional(),
    CORRESPONDENCE_FROM_NAME: z.string().min(1).optional(),
    /** Zadatel v dopise: FIRMA kancelare, nikdy makler jako osoba (K3b-11). */
    CORRESPONDENCE_REQUESTER_NAME: z.string().min(1).optional(),
    CORRESPONDENCE_REQUESTER_ICO: z.string().min(1).optional(),
    CORRESPONDENCE_REQUESTER_ADDRESS: z.string().min(1).optional(),
    CORRESPONDENCE_REQUESTER_EMAIL: z.string().min(3).optional(),
    CORRESPONDENCE_REQUESTER_PHONE: z.string().min(1).optional(),
    /** Lhuta, o kterou adresata zadame (a po ktere se navrhne pripominka). */
    CORRESPONDENCE_DUE_DAYS: z.coerce.number().int().min(1).max(120).default(14),
    /**
     * Krok P1REP2 (GO R.B. 19. 10. 2026): zadost o vyjadreni spravcu siti
     * vznikne SAMA po kazdem dobehnutem proverení - komu psat se bere z faktu
     * DTM, ne z kliknuti cloveka. Bez priznaku bezi system jako dosud
     * (zadost zaklada clovek v adminu).
     */
    UTILITY_AUTO_REQUEST: z
        .enum(['true', 'false'])
        .default('false')
        .transform((value) => value === 'true'),
    /**
     * Krok P1REP2: OSTRE ODESLANI. `false` = e-maily zustanou ve fronte
     * „dotazy ke schvaleni" (stav po kroku A4). `true` = schvaleni udela
     * priznak a posta odejde; v auditu je u kazde zpravy duvod
     * `AUTO_APPROVE_REASON` i servisni ucet behu. Zapina jen clovek na serveru.
     */
    UTILITY_AUTO_SEND: z
        .enum(['true', 'false'])
        .default('false')
        .transform((value) => value === 'true'),
    /**
     * Krok S5 (one deploy): git sha bezici instance, nastavuje deploy skript
     * (ops/deploy-preview.sh). Bez nej /healthz a /version padaji na `git
     * rev-parse` fallbacku (dev/test).
     */
    REOS_SHA: z.string().min(1).optional(),
    /** Krok S5: ISO cas buildu pro /version; nastavuje deploy skript. */
    REOS_BUILT_AT: z.string().min(1).optional(),
    /**
     * Krok C1 (commerce): poskytovatel platebni brany. Krok C5 pridal `stripe`
     * (rozhodnuti R.B. 23. 9. 2026); `fake` je podepsany sandbox pro testy
     * a preview a zustava VYCHOZI - ostra brana se zapina vedome.
     */
    COMMERCE_PAYMENT_PROVIDER: z.enum(['fake', 'stripe', 'paygw']).default('fake'),
    /** Alias zadani C5 (`PAYMENT_PROVIDER`); ma prednost pred delsim jmenem vyse. */
    PAYMENT_PROVIDER: z.enum(['fake', 'stripe', 'paygw']).optional(),
    /**
     * Krok COMGATE1: RedHand pay-gateway (Comgate) — rozhodnuti R.B.
     * 22. 10. 2026. Stripe ucet je sdileny s Hrady pro deti a predstavuje se
     * zakaznikovi cizi znackou; pay-gateway je vlastni sluzba RedHandu.
     *
     * `paygw` BEZ kteréhokoli z tri udaju nize = proces NENASTARTUJE
     * (`superRefine` nize). Fail-closed: platebni brana, kterou nejde overit,
     * je horsi nez nedostupna objednavka.
     */
    PAYGW_URL: z.string().url().optional(),
    PAYGW_API_KEY: z.string().min(1).optional(),
    /** Tajemstvi pro podpis webhooku brany (`Paygw-Signature`). */
    PAYGW_WEBHOOK_SECRET: z.string().min(1).optional(),
    /**
     * Krok C5: Stripe. Klice VYHRADNE z prostredi, nikdy v kodu ani v repu.
     * `sk_live_` bez `COMMERCE_LIVE_PAYMENTS=true` shodi start aplikace
     * (pojistka rezimu, viz `configureCommerceFromEnv`).
     */
    STRIPE_SECRET_KEY: z.string().min(1).optional(),
    /** Verejny (publishable) klic - z definice viditelny v prohlizeci. */
    STRIPE_PUBLISHABLE_KEY: z.string().min(1).optional(),
    /**
     * Tajemstvi VLASTNIHO webhook endpointu REOS (`whsec_…`). NENI sdilene
     * s webhookem Hradu pro deti, i kdyz jde o tentyz Stripe ucet.
     */
    STRIPE_WEBHOOK_SECRET: z.string().min(1).optional(),
    /** Pripona na vypisu z uctu platce (`statement_descriptor_suffix`). */
    STRIPE_STATEMENT_SUFFIX: z.string().trim().min(1).max(22).optional(),
    /**
     * Krok KR1: VLASTNI webhook endpoint pro dobiti kreditu AI - stejny Stripe
     * ucet jako commerce (`STRIPE_SECRET_KEY`), ale jiny `whsec_…` (kazdy
     * endpoint ma u Stripe vlastni podpisovy secret). Bez nej webhook konci
     * 503 (fail-closed, stejny vzor jako `STRIPE_WEBHOOK_SECRET` u commerce).
     */
    AI_CREDITS_STRIPE_WEBHOOK_SECRET: z.string().min(1).optional(),
    /**
     * Krok KR1: ktera brana dobiji kreditu kartou. VYCHOZI `fake` (sandbox,
     * zadna sit) - na rozdil od `PAYMENT_PROVIDER` u commerce je vychozi
     * hodnota bezpecna i kdyz Stripe ucet (sdileny s commerce) je v ostrem
     * rezimu, protoze tuhle featuru jeste nikdo vedome nezapnul.
     */
    AI_CREDITS_PAYMENT_PROVIDER: z.enum(['fake', 'stripe']).default('fake'),
    /**
     * Krok KR1 — REOS (zatim) nema cross-tenant superadmin roli/UI; dokud
     * nevznikne (ROZHODNUTI KR1-2), chrani `/internal/ai-admin/*` sdilene
     * tajemstvi v hlavicce `x-superadmin-token`. Bez nej cesta konci 503
     * (fail-closed, stejny vzor jako chybejici platebni klice).
     */
    SUPERADMIN_TOKEN: z.string().min(16).optional(),
    /**
     * Krok TRYCHTYR1 — PREHLED TRYCHTYRE WEBU (`/interni/provoz`) cte cisla
     * z Umami API. Vsechny tri promenne jsou NEPOVINNE: bez nich obrazovka
     * rekne „mereni neni pripojene" a necte nic (stejny vzor jako
     * `ASISTENTKA_CORE_URL`) — vymyslet cisla by bylo horsi nez prazdno.
     *
     * `UMAMI_API_KEY` je SECRET (zije jen v prostredi, nikdy v repu ani
     * v logu). `UMAMI_API_WEBSITE_ID` secret neni — tentyz identifikator
     * nese verejny bundle webu (`VITE_PUBLIC_UMAMI_ID`) —, ale default tu
     * nema co delat: server bez vyslovne konfigurace nema volat cizi sluzbu.
     */
    UMAMI_API_URL: z.string().url().optional(),
    UMAMI_API_KEY: z.string().min(8).optional(),
    UMAMI_API_WEBSITE_ID: z.string().min(8).optional(),
    /**
     * Krok AS3 — Asistentka (core na serveru 138) placena z KREDITU REOS
     * (`ai_key_source = app_credits`) dostane pres `/internal/assistant-provider`
     * TENHLE klic REOS. Vlastni promenna (ne sdileny `ANTHROPIC_API_KEY`
     * ostatnich AI cest), aby spotreba Asistentky sla u poskytovatele oddelit.
     * Bez nej kredity Asistentce nic nepusti (503 `NOT_CONFIGURED`).
     */
    ASSISTANT_ANTHROPIC_API_KEY: z.string().min(20).optional(),
    /**
     * Modely, ktere provider Asistentce vrati. MUSI byt v ceniku KR1
     * (`MODEL_PRICING`), jinak by tah nesel vyuctovat — provider pak vrati
     * 503 `NOT_CONFIGURED`, ne neucetni klic.
     */
    ASSISTANT_MODEL: z.string().min(3).default('claude-sonnet-5'),
    ASSISTANT_MODEL_LIGHT: z.string().min(3).default('claude-haiku-4-5-20251001'),
    /**
     * Krok AS3 bod 5 — plocha „Asistentka" v adminu proxyuje na asistentku-core
     * (server 138, 127.0.0.1:3165). Bez URL plocha rekne „Asistentka zatím není
     * připojená" (503 NOT_CONNECTED), ne chybu. Token = projektovy klic core
     * pro admin proxy (ne token connection).
     */
    ASISTENTKA_CORE_URL: z.string().url().optional(),
    ASISTENTKA_CORE_TOKEN: z.string().min(16).optional(),
    /**
     * Krok MRGT1 (ROZHODNUTI MRGT1-INTERNAL-SOCKET) — VLASTNI loopback port,
     * na kterem API/gateway obsluhuje `/internal/assistant-*` (cesty, ktere
     * vraceji API klic kancelare nebo REOS). Verejny vhost ho neproxuje,
     * takze „jsem z loopbacku" se pozna podle SOCKETU, na ktery pozadavek
     * prisel, ne podle hlavicky (viz `isInternalListenerRequest`).
     *
     * Proc ne jen `remoteAddress`: na preview jde nginx na `127.0.0.1:3107`,
     * takze KAZDY pozadavek zvenku ma remoteAddress 127.0.0.1 a jedinou
     * obranou byla PRITOMNOST `X-Forwarded-For` — tedy fail-open na hlavicce,
     * kterou pet z osmi existujicich `location` bloku preview vubec nenastavuje
     * (`/healthz`, `/version`, `/oauth/register`, `/oauth/token`,
     * `/.well-known/*`). Bez portu by jediny novy prefixovy `location` stacil
     * k vydani klice.
     *
     * Nenastaveno = `/internal/assistant-*` nejde zavolat ODNIKUD (fail-closed;
     * Asistentka proste jeste neni zapojena, viz AS3c).
     */
    ASSISTANT_INTERNAL_PORT: z.coerce.number().int().positive().optional(),
    /** Totez pro MCP gateway: core vola `origin(mcp_url)/internal/*`, proto ma
     * gateway vlastni interni port a `mcp_url` connection na nej miri. */
    MCP_GATEWAY_INTERNAL_PORT: z.coerce.number().int().positive().optional(),
    /**
     * Krok E2E2 bod 8 — KAM GATEWAY VOLA API (volani SLUZBA-SLUZBA).
     *
     * Nalez 5. 10. 2026: gateway volala API pres `REOS_ISSUER_URL`, tedy pres
     * VEREJNOU domenu. Na preview je cely vhost za basic auth, takze kazdy
     * nastroj `reos.*` dostal 401 — vcetne MCP konektoru z Claude.ai.
     * Obchazelo se to vyjimkou v nginx (`geo $reos_from_host`), co je ale
     * dira v obrane administrace, ne reseni.
     *
     * `REOS_ISSUER_URL` zustava tim, cim byl: VEREJNOU identitou issuera
     * v metadatech OAuth a v `aud`/`iss` tokenu. Tahle promenna rika jen,
     * kam se posila HTTP pozadavek — nenastavena = beze zmeny (issuer).
     */
    REOS_API_INTERNAL_URL: z.string().url().optional(),
    /**
     * Krok C5: vedomy souhlas s ostrymi platbami. Bez `true` API odmitne
     * nastartovat s ostrym klicem - aby se `sk_live_` nedostal do preview
     * omylem (kopie .env, spatny profil nasazeni).
     */
    COMMERCE_LIVE_PAYMENTS: z
        .enum(['true', 'false'])
        .default('false')
        .transform((value) => value === 'true'),
    /**
     * Krok C5: aplikacni REST API MyUcta (instance `hradyprodeti`, image
     * redhand/myucto:6.9.1-rh2) pro systemoveho tenanta `DIRECT`. Kazda firma
     * (tenant) ma vlastni pripojeni - tohle je JEN vychozi pripojeni RedHandu
     * (viz `accountingFor()` v @reos/finance).
     */
    MYUCTO_APP_BASE_URL: z.string().url().optional(),
    /** Samostatny PAT pro REOS (`mi_pat_…`) - NIKDY sdileny s Hrady pro deti. */
    MYUCTO_APP_PAT: z.string().min(1).optional(),
    /** `X-Supplier-Id` - firma, v jejimz kontextu doklad vznika. */
    MYUCTO_APP_SUPPLIER_ID: z.coerce.number().int().positive().optional(),
    /**
     * Volitelne prebiti sazby DPH z ciselniku MyUcta (`/codebooks/vat-rates`).
     * Bez nej si adapter sazbu dohleda podle procenta z objednavky.
     */
    MYUCTO_APP_VAT_RATE_ID: z.coerce.number().int().positive().optional(),
    /**
     * Krok FAK1: vlastni instance MyUcta pro fakturaci REOS tenantu (CZ ONLY -
     * Doplnek 28. 9. 21:50 R.B. "Slovensko se NEDELA"). NENASTAVENO = fakturace
     * se v adminu ukaze jako "pripravujeme", nic nespadne (`myucto-config.ts`).
     * Nazvy jsou dane rozhodnutim R.B. 28. 9. 21:40, viz docs/agent-briefs/
     * step-FAK1-fakturace-zaklad.md - NEMENIT bez ADR, uz existuje nasazeni
     * s temito nazvy v `/opt/reos-preview/.env` na Ryzenu.
     */
    REOS_BILLING_MYUCTO_CZ_URL: z.string().url().optional(),
    /** Id dodavatele-provozovatele (Hrady pro deti s.r.o.) - vychozi `X-Supplier-Id` servisniho PAT. */
    REOS_BILLING_MYUCTO_CZ_SUPPLIER_ID: z.coerce.number().int().positive().optional(),
    /** Servisni PAT s roli admin pro instanci - NIKDY do gitu/logu (`redactText`). */
    REOS_BILLING_MYUCTO_CZ_PAT: z.string().min(1).optional(),
    /**
     * Cesta k overenemu fleet skriptu, ktery na serveru 138 zaklada NOVEHO
     * dodavatele v instanci + vyrazi mu izolovany PAT (vzor Maner
     * `add-supplier-to-instance.sh` - zakladani dodavatele NENI cast verejneho
     * `/api/v1`, viz `provisioning-port.ts`). Bez skriptu na hostu provisioning
     * jen ceka ve fronte (`SupplierProvisionerUnavailable`), nic nespadne.
     */
    REOS_BILLING_PROVISION_SCRIPT_CZ: z.string().min(1).optional(),
    /**
     * ROZHODNUTI (krok FAK1): slug instance ve fleetu pro fleet skript vyse.
     * Zadani jmenuje instanci `myucto-reos-cz` (Rozhodnuti R.B. 28. 9.) - vychozi
     * hodnota je odvozena timto nazvem; kdyz na hostu 138 sedi jiny slug, prebije
     * se env, ne uprava kodu.
     */
    REOS_BILLING_PROVISION_SLUG_CZ: z.string().min(1).default('reos-cz'),
    /** Interval sweepu fronty provisioningu dodavatelu v apps/worker (0 = vypnuto). */
    REOS_BILLING_PROVISION_INTERVAL_MS: z.coerce.number().int().min(0).default(60_000),
    /**
     * Krok FAK4: cesta k overenemu fleet skriptu, ktery zapise bankovni ucet
     * dodavatele do instance MyUcta (`set-bank-account.sh <slug> <supplier_id>
     * <mena> <iban>`). Bankovni ucty dodavatele NEJSOU ve verejnem
     * `PUT /api/v1/settings/supplier` - viz `bank-sync-port.ts`. Bez skriptu
     * uloha jen ceka ve fronte (`SupplierBankSyncUnavailable`), ucet zustava
     * ulozeny v REOS a UI to rekne nahlas.
     */
    REOS_BILLING_BANK_SCRIPT_CZ: z.string().min(1).optional(),
    /**
     * Krok FAK4: interval sweepu fronty `finance_supplier_sync_jobs` (vzhled
     * a bankovni ucet -> MyUcto) v apps/worker (0 = vypnuto). Vlastni interval,
     * ne provisioningovy: tyhle ulohy jsou levne a maji se dohnat driv nez
     * zalozeni noveho dodavatele.
     */
    REOS_BILLING_SYNC_INTERVAL_MS: z.coerce.number().int().min(0).default(60_000),
    /**
     * Tajemstvi pro HMAC podpis webhooku fake brany. Bez nej se brana
     * nekonfiguruje a `POST /v1/public/payments/webhook/:provider` vraci 503 -
     * NIKDY se nepodepsany webhook neprijme. Zadna vychozi hodnota v kodu.
     */
    COMMERCE_FAKE_PAYMENT_SECRET: z.string().min(16).optional(),
    /** Verejna URL stranky objednavky (zaklad redirectu brany). */
    COMMERCE_PUBLIC_BASE_URL: z.string().url().optional(),
    /** Turnstile (BotGuard) - klic VYHRADNE z prostredi. */
    TURNSTILE_SECRET_KEY: z.string().min(1).optional(),
    /**
     * Krok C2: verejny (site) klic Turnstile. Na rozdil od tajemstvi vyse je
     * z definice viditelny v prohlizeci - API ho proto vydava v
     * `GET /v1/public/config`, aby ho verejny web nemusel mit zapeceny v buildu.
     * Bez nej se widget nevykresli a objednavka konci 400 na chybejicim tokenu.
     */
    TURNSTILE_SITE_KEY: z.string().min(1).optional(),
    /**
     * Krok C2: znacka verejneho webu. Neni to prekladany text, ale konfigurace
     * nasazeni - proto env, ne i18n katalog.
     *
     * Krok BRAND1: vychozi hodnota je jmeno produktu. `@reos/config` si ho
     * NEIMPORTUJE z `@reos/i18n` schvalne — config je nejnizsi balicek
     * (jen zod) a kazdy, kdo ho nacte, by tahnul i katalog zprav. Ze se obe
     * hodnoty nerozejdou, meri `packages/platform/i18n/test/product-brand.spec.ts`
     * (tyz vzor jako u `index.html` a `manifest.webmanifest`, ktere take
     * nemohou nic importovat).
     */
    PUBLIC_BRAND_NAME: z.string().min(1).default('Makléřov'),
    /**
     * Rate limit verejneho objednavkoveho endpointu (pozadavku za minutu na IP).
     * Vyrazne prisnejsi nez globalni RATE_LIMIT_MAX - kazda objednavka konci
     * placenym behem, takze tenhle formular je nejdrazsi vstup do systemu.
     */
    COMMERCE_PUBLIC_RATE_LIMIT_MAX: z.coerce.number().int().positive().default(10),
    /** Je prodavajici platce DPH? Rozhoduje o rozpadu ceny (viz pricing.ts). */
    COMMERCE_VAT_PAYER: z
        .enum(['true', 'false'])
        .default('true')
        .transform((value) => value === 'true'),
    /** Splatnost zalohove faktury ve dnech (VS = cislo objednavky). */
    COMMERCE_PROFORMA_DUE_DAYS: z.coerce.number().int().min(1).max(60).default(7),
    /**
     * Krok C2: ucet pro zalohovou platbu (IBAN). Bez nej stranka stavu
     * objednavky QR platbu NEUKAZE a rekne, ze pokyny prijdou e-mailem -
     * radeji zadny QR nez QR na vymysleny ucet. Neni to secret (cislo uctu
     * je na kazde fakture), ale je to konfigurace nasazeni.
     */
    COMMERCE_BANK_IBAN: z.string().trim().min(15).max(34).optional(),
    /**
     * Krok C3: tajemstvi pro podpis odkazu na PDF reportu (HMAC-SHA256, stejny
     * vzor jako COMMERCE_FAKE_PAYMENT_SECRET). Bez nej se doruceni nekonfiguruje
     * a beh konzumenta selze (retry pres BullMQ) - zadny nepodepsany odkaz.
     */
    COMMERCE_DELIVERY_LINK_SECRET: z.string().min(16).optional(),
    /** Platnost odkazu na PDF reportu ve dnech (vychozi 30, zadani C3). */
    COMMERCE_DELIVERY_LINK_TTL_DAYS: z.coerce.number().int().min(1).max(365).default(30),
    /**
     * Krok P1MAP4 bod 2: tajemstvi pro PODPIS NOSICE UROVNE verejne mapy
     * (`@reos/map/grant`). geo-api, ktera zamcena data servíruje, nema
     * identitu ani vazbu na REOS DB — o naroku proto rozhoduje apps/api
     * a podpis je jediny zpusob, jak to geo-api doveri.
     *
     * TATAZ hodnota musi byt v `GEO_MAP_ACCESS_SECRET` u geo-api. Bez nej
     * se nosic nevydava (`accessToken: null`) a mapa se chova jako dosud —
     * fail-closed, zadne nasazeni se nerozbije.
     */
    COMMERCE_MAP_ACCESS_SECRET: z.string().min(16).optional(),
    /** Platnost nosice urovne v minutach (vychozi 30; obnovuje se dotazem na API). */
    COMMERCE_MAP_ACCESS_TTL_MINUTES: z.coerce.number().int().min(1).max(1_440).default(30),
    /**
     * Krok C3: absolutni zaklad URL API pro odkaz na PDF v e-mailu. Bez neho
     * je odkaz relativni (funguje v testech, ne ve skutecnem e-mailu) - viz
     * Nalezy v REPORT-step-C3.md.
     */
    COMMERCE_API_BASE_URL: z.string().url().optional(),
    /**
     * Krok C3: adresa RedHand, kam chodi upozorneni na SLA a attention polozky
     * (chybejici notifikacni kanal - ROZHODNUTI C3, viz REPORT-step-C3.md).
     */
    COMMERCE_OPS_EMAIL: z.string().email().optional(),
    /**
     * Krok WEB1: zaklad URL VEREJNEHO WEBu (`apps/web-public`) pro jediny odkaz,
     * ktery commerce posila mimo API - overovaci magic link na `/aktivace`.
     * Bez nej se e-mail vbecne neposle (raději ticho nez rozbity odkaz).
     */
    COMMERCE_PUBLIC_SITE_URL: z.string().url().optional(),
    /**
     * Krok TRIAL1 (rozhodnuti R.B. 9. 10. 2026 20:34, rusi bod 8 kroku D3):
     * „Zkusebni 2 mesice dame bez karty." Delka zkusebniho obdobi kancelare
     * ve DNECH je JEDINA hodnota, ze ktere zije cely produkt — aktivace,
     * pruh v aplikaci, pripominky e-mailem i veta na webu. Vychozi 60 dni
     * (= 2 mesice); `0` = zkusebni obdobi nedavat.
     *
     * Zadne cislo dni ani mesicu NESMI stat v textu katalogu prekladu —
     * hlida to `test/d3-zadny-trial.spec.ts`.
     */
    COMMERCE_OFFICE_TRIAL_DAYS: z.coerce.number().int().min(0).max(730).default(60),
    /**
     * Krok WEB1: platnost overovaciho odkazu v hodinach (jednorazovy token,
     * 192 bitu, v DB jen sha256). Vychozi 72 h - lide nekliknou hned.
     */
    COMMERCE_OFFICE_TOKEN_TTL_HOURS: z.coerce.number().int().min(1).max(720).default(72),
    /**
     * Krok WEB1: velikost kuponu kampane vydaneho pri registraci kancelare
     * ("kredit na velky report"). `0` = kupon nevydavat (zadani: 1 na ICO,
     * coz je i tak vynuceno UNIQUE na `commerce_coupons.ico`).
     */
    COMMERCE_OFFICE_COUPON_REPORTS: z.coerce.number().int().min(0).max(50).default(1),
    /** Krok WEB1: platnost kuponu kampane v dnech. */
    COMMERCE_OFFICE_COUPON_VALID_DAYS: z.coerce.number().int().min(1).max(730).default(60),
    /** Krok WEB1: produkt, na ktery kupon kampane plati. */
    COMMERCE_OFFICE_COUPON_PRODUCT: z.string().min(1).default('KOMPLET'),
    /**
     * Krok D7 (R27/R23): kolik LOKALIT (malych reportu) dava zaplaceny mesic
     * tarifu Start, resp. clenstvi Makler. Zadani D7: „Pocet Lokalit v Startu
     * i v clenstvi je KONFIGURACE (ne konstanta v kodu)". Vychozi hodnoty
     * potvrdil R.B. krokem DOLAD1 (R28, 9. 10. 2026): 15/15, podle konkurence.
     * Kupon na 1 Komplet u clenstvi je proti tomu obchodni slib z R23, ne
     * ladici cislo — v env neni.
     */
    COMMERCE_PLAN_START_LOCALITY_REPORTS: z.coerce.number().int().min(0).max(500).default(15),
    COMMERCE_PLAN_MAKLER_LOCALITY_REPORTS: z.coerce.number().int().min(0).max(500).default(15),
    /**
     * Krok CEN2 (ceny schvalene R.B. 21. 10. 2026): tytez pocty pro tarify
     * KANCELAR (30 Lokalit) a KANCELAR + KOMPLET (50). Stejny duvod jako
     * u Startu — pocet Lokalit je konfigurace, kupony na Komplet obchodni
     * slib. Rocni varianta tarifu zadny vlastni pocet NEMA: ma stejne naroky
     * jako mesicni (`planGrantsFor` prevadi `*_YEARLY` na zakladni kod).
     */
    COMMERCE_PLAN_KANCELAR_LOCALITY_REPORTS: z.coerce.number().int().min(0).max(500).default(30),
    COMMERCE_PLAN_KANCELAR_PLUS_LOCALITY_REPORTS: z.coerce
        .number()
        .int()
        .min(0)
        .max(500)
        .default(50),
    /**
     * Krok WEB1: verze OBCHODNICH PODMINEK, se kterymi je souhlas pri
     * registraci. Prazdna = jeste neschvaleno R.B. -> registrace je zavrena
     * (souhlas musi odkazovat na konkrétni verzi, ne na „aktualni text").
     */
    COMMERCE_OFFICE_TERMS_VERSION: z.string().default(''),
    /**
     * Krok WEB1: verze ZPRACOVATELSKE SMLOUVY (GDPR cl. 28) pro kancelare.
     * Stejne jako u podmínek: prazdna = zatim jen navrh.
     */
    COMMERCE_OFFICE_DPA_VERSION: z.string().default(''),
    /**
     * Krok GDPR1: verejny kontaktni e-mail provozovatele REOS (zpracovatele)
     * pro pravni stranky webu a zaznam o cinnostech zpracovani kancelare.
     * JEDINY zdroj — zadny e-mail se nepise natvrdo do kodu. Bez promenne
     * se misto kontaktu zobrazi citelne „doplnit" (nikdy pad).
     *
     * Krok MRGD1: odtud ho bere i User-Agent kampanoveho robota
     * (`campaignUserAgent` v `apps/worker/src/campaign/http.ts`) — drive tam
     * byl natvrdo. Patička obchodniho sdeleni ma VLASTNI povinnou promennou
     * `REOS_OPERATOR_CONTACT_EMAIL`, duvod je u ni.
     */
    REOS_PUBLIC_CONTACT_EMAIL: z.string().email().optional(),
    /**
     * Dotaz do insolvencniho rejstriku (ISIR) na VLASTNIKA nemovitosti.
     *
     * Krok MRGD1 (rozhodnuti R.B. 4. 10. 2026 „ISIR zapni pro vsechny"):
     * VYCHOZI `on`, takze promenna muze chybet. `off` je NOUZOVY VYPINAC —
     * jedina hodnota, ktera lustraci zastavi (semantika `disabled_when`
     * z kroku ISIR1b, fail-OPEN). Zapsana jako enum proto, aby preklep
     * (`ISIR_OWNER_QUERY=vypnuto`) spadl pri startu, a ne tise nechal
     * lustraci zapnutou.
     *
     * Pravidla dotazu (B2C fyzickou osobu NIKDY, podnikatel jen podle IČO
     * a vysledek ano/ne) drzi `isirOwnerQueryDecision` v `@reos/duediligence`
     * — ta jsou KOD, ne konfigurace (ROZHODNUTI GDPR1-4).
     *
     * Krok MRGI2 (napojeni ISIR1): hodnotu NECTE adapter odtud. Nese ji
     * `disabled_when` zdroje `isir.ws.osoba` v SOURCE-REGISTRY (pravidlo K0a:
     * o zdroji rozhoduje registr, ne adapter) a adapter ji predava JEDINEMU
     * pravidlu `isirOwnerQueryDecision`. Tahle polozka je tu proto, aby
     * preklep spadl pri startu — a protoze oba merge (MRGD1 i ISIR1b) ji
     * pridaly se stejnym schematem, zustava JEDNA.
     */
    ISIR_OWNER_QUERY: z.enum(['on', 'off']).default('on'),
    /**
     * Krok NAP8 bod 1 (A18): dotaz do Registru neplatnych dokladu (MV CR).
     *
     * Sluzba je ZDARMA, BEZ REGISTRACE a dotaz je GET, takze vychozi hodnota
     * je `on` a promenna muze chybet. `off` je NOUZOVY VYPINAC (tataz
     * semantika jako `ISIR_OWNER_QUERY`): sluzba pak vrati
     * `service_unavailable`, tedy „nepodarilo se overit" — NIKDY „doklad je
     * platny". Enum proto, aby preklep spadl pri startu.
     *
     * O zdroji samotnem dal rozhoduje registr (`mv.neplatne_doklady`,
     * pravidlo K0a) — tahle promenna rika jen, jestli se smi jit na sit.
     */
    INVALID_DOCUMENT_CHECK: z.enum(['on', 'off']).default('on'),
    /** Datum platnosti verze podmínek (ISO `YYYY-MM-DD`); bez nej stranka píše „návrh“. */
    COMMERCE_OFFICE_TERMS_EFFECTIVE_DATE: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .optional(),
    /** Datum platnosti verze ZPS (ISO `YYYY-MM-DD`). */
    COMMERCE_OFFICE_DPA_EFFECTIVE_DATE: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .optional(),
    /** Krok C3: interval SLA sweepu v apps/worker (0 = vypnuto, jen rucni/testovaci volani). */
    COMMERCE_SLA_SWEEP_INTERVAL_MS: z.coerce.number().int().min(0).default(300_000),
    /**
     * Krok TRIAL1: interval sweepu pripominek konce zkusebniho obdobi
     * (14 dni / 3 dny / v den konce) v apps/worker.
     *
     * VYCHOZI 0 = VYPNUTO, zamerne: REOS nic neposila bez GO R.B. Zapnuti je
     * rozhodnuti nasazeni, ne vychozi stav kodu — stejny postoj jako u
     * politiky uvolneni reportu (`COMMERCE_RELEASE_*`).
     */
    COMMERCE_TRIAL_NOTICE_INTERVAL_MS: z.coerce.number().int().min(0).default(0),
    /**
     * Krok KP1: zaklad odkazu klientskeho formulare (magic link). Bez nej se
     * vraci relativni cesta `/klient/<token>` - makler ji vidi a doplni sam.
     * Radeji neuplny odkaz nez odkaz na vymysleny host (tyz princip jako
     * COMMERCE_API_BASE_URL a QR na nenakonfigurovany ucet).
     */
    CLIENT_INTAKE_BASE_URL: z.string().url().optional(),
    /**
     * Krok KP1: doba uchovani podani klienta ve dnech (zadani bod 6 "doba
     * uchovani v konfiguraci"). Po uplynuti retencni job smaze SIFROVANOU cast
     * a osobni udaje, radek pozvanky a casova osa zustavaji jako dukaz o AML
     * identifikaci. Vychozich 10 let = archivacni povinnost AML zakona.
     */
    CLIENT_INTAKE_RETENTION_DAYS: z.coerce.number().int().min(1).default(3650),
    /**
     * Krok KP1: interval retencniho jobu klientskych podani v apps/worker
     * (0 = vypnuto). DRY-RUN je DEFAULT, stejne jako u retence dokumentu -
     * ostry beh se zapina vedome (`CLIENT_INTAKE_RETENTION_DRY_RUN=false`).
     */
    CLIENT_INTAKE_RETENTION_INTERVAL_MS: z.coerce.number().int().min(0).default(86_400_000),
    CLIENT_INTAKE_RETENTION_DRY_RUN: z
        .enum(['true', 'false'])
        .default('true')
        .transform((value) => value === 'true'),
    /**
     * Krok KP1: cesta k `tesseract` pro cteni MRZ a predni strany dokladu.
     * Bez binarky se snimek nevytezi a formular nabidne rucni vyplneni
     * (pravidlo L0: "nevytezeno" je lepsi nez "vytezeno z niceho").
     */
    TESSERACT_BINARY: z.string().min(1).optional(),
    /**
     * Krok ES1: zaklad odkazu k PODPISU smlouvy (`https://…/podpis`). Bez nej
     * se vraci relativni `/podpis/<token>` - makler ho vidi a doplni sam,
     * stejne jako u CLIENT_INTAKE_BASE_URL. Radeji neuplny odkaz nez odkaz na
     * vymysleny host: tenhle odkaz vede k PRAVNIMU AKTU.
     */
    SIGNATURE_BASE_URL: z.string().url().optional(),
    /**
     * Krok ES2 bod 4: adresa VEREJNEHO WEBU (`https://reos-web.…`), ze ktere
     * se skladaji odkazy pro klienta, kdyz neni nastavena konkretnejsi
     * `SIGNATURE_BASE_URL` / `CLIENT_INTAKE_BASE_URL`.
     *
     * DUVOD: na preview nebyla nastavena ani jedna, takze API vracelo
     * `/podpis/KT5P…` — relativni cestu. Makler ji zkopiroval do zpravy a
     * klientovi poslal odkaz, ktery nikde neotevre. Jedna promenna pro cely
     * verejny web je navic to, co administrator opravdu zna (vhost), zatimco
     * „zaklad odkazu k podpisu" si musel domyslet. Kdyz chybi i tahle, pouzije
     * se `REOS_PUBLIC_WEB_BASE_URL` (tyz verejny web, jen pojmenovany kampanemi) —
     * dve promenne pro tutéž adresu nejsou zamer, jen poradi hledani.
     */
    WEB_PUBLIC_URL: z.string().url().optional(),
    /** Krok ES1: platnost odkazu k podpisu ve dnech (vychozi 7, viz levels.ts). */
    SIGNATURE_LINK_TTL_DAYS: z.coerce.number().int().min(1).max(90).default(7),
    /**
     * Krok ES1 bod 3: poskytovatele elektronickeho podpisu. KLICE JSOU SECRET
     * a patri do SOPS (`ops/secrets/README.md`), nikdy do repa ani do testu.
     *
     * Kdyz dvojice (URL + klic) chybi, cesta se v kanceláři NEDA pouzit:
     * `signatureProviderFor()` vrati `null`, sezeni na te ceste se ZALOZIT
     * NEDA (409 `signature-route-not-configured`) a makler to vidi jako
     * "cesta neni nastavena". To je zamer - tichy fallback na podpis prstem by
     * znamenal prosty podpis vydavany za zaruceny nebo kvalifikovany.
     *
     * Tvar HTTP rozhrani partnera je v `signature/provider.ts` a je to
     * PLACEHOLDER do doby, nez bude partnerska dokumentace - proto se do
     * ostreho provozu zapina jen vedome nastavenim klice.
     */
    SIGNI_API_URL: z.string().url().optional(),
    SIGNI_API_KEY: z.string().min(1).optional(),
    BANKID_SIGN_API_URL: z.string().url().optional(),
    BANKID_SIGN_API_KEY: z.string().min(1).optional(),
    BANKID_QSIGN_API_URL: z.string().url().optional(),
    BANKID_QSIGN_API_KEY: z.string().min(1).optional(),
    /**
     * Krok A5 (politika uvolneni reportu zakaznikovi, AGENT-SYSTEM.md §3 - role
     * Revizor). Prvnich N reportu tenanta uvolnuje VZDY clovek; potom rozhoduje
     * namatkovy vzorek a spoustece (NEEDS_REVIEW, rozpor Oponenta, naklad nad
     * stropem, novy typ nemovitosti - viz release-policy.ts).
     *
     * `0` znamena "pilotni okno uz skoncilo" - NENI to vypnuti politiky
     * (spoustece plati dal a namatkovy vzorek taky).
     */
    COMMERCE_RELEASE_FIRST_N: z.coerce.number().int().min(0).max(10_000).default(20),
    /** Podil reportu uvolnovanych clovekem po pilotnim okne (procenta 0-100). */
    COMMERCE_RELEASE_SAMPLE_PERCENT: z.coerce.number().int().min(0).max(100).default(10),
    /**
     * Strop nakladu jedne zakazky v Kc, nad kterym report vzdy vidi clovek.
     * Vychozi hodnota je stejna jako `AUDIT_ROLES_BUDGET_CZK` (A3), ale je to
     * VLASTNI promenna: revizni prah smi byt nizsi nez tvrdy strop, ktery
     * pipeline rolí zastavi.
     */
    COMMERCE_RELEASE_COST_CAP_CZK: z.coerce.number().min(0).default(40),
    /**
     * Krok K0f: WSDP 3.1 (dalkovy pristup do katastru, SOAP). NENASTAVENO =
     * adapter vypnuty a lidska cesta z K0e bezi dal - to je vychozi stav, dokud
     * nema firma ostry zakaznicky ucet. `trial` = zkusebni prostredi CUZK
     * (fiktivni data KP Prachatice, sestavy zdarma), `prod` = ostry ucet.
     * Endpoint se NEBERE odtud, ale z registru zdroju (pravidlo K0a) - env
     * rozhoduje jen o tom, KTERY z overenych endpointu registru se pouzije.
     */
    WSDP_ENV: z.enum(['trial', 'prod']).optional(),
    /** Prihlasovaci jmeno zakaznickeho uctu WSDP. Do repa NIKDY - jen env. */
    WSDP_USER: z.string().min(1).optional(),
    /** Heslo zakaznickeho uctu WSDP. Nikdy se neloguje ani nezapisuje do DB. */
    WSDP_PASS: z.string().min(1).optional(),
    /**
     * Strop nakladu na JEDEN beh auditu v Kc. Sestava, ktera by ho prekrocila,
     * se NESTAHNE (a tim se nezauctuje) - viz `wsdp/budget.ts`. Vychozi 300 Kc
     * podle zadani K0f bodu 4.
     */
    WSDP_MAX_CZK_PER_RUN: z.coerce.number().int().min(0).default(300),
    /** Timeout jednoho SOAP volani WSDP v ms. */
    WSDP_TIMEOUT_MS: z.coerce.number().int().min(1_000).default(30_000),
    /**
     * Krok CENY1 — PLNENI DATOVE SADY REALIZOVANYCH CEN (placene,
     * `generujCenoveUdajeDleKu`, 10 Kc za k. u. a mesic).
     *
     * Vychozi FALSE: cely retezec (stazeni -> cisteni -> doparovani vymery ->
     * agregace) je hotovy a otestovany, ale NIC se nestahuje, dokud se
     * prepinac nezapne. Beh nad `WSDP_ENV=trial` je zdarma a na fiktivnich
     * datech KP Prachatice; po pridelení ostreho uctu staci zmenit
     * `WSDP_ENV`, `WSDP_USER` a `WSDP_PASS` — kod se nemeni.
     */
    WSDP_PRICES_LIVE: z
        .enum(['true', 'false'])
        .default('false')
        .transform((value) => value === 'true'),
    /**
     * Krok CENY1 — SOUHLAS ČÚZK SE SIRENIM UDAJU KATASTRU (§ 11a vyhl.
     * 358/2013, ucinne od 1. 7. 2024). Vychozi FALSE.
     *
     * Rozhoduje JEN o tom, co smi z cenovych udaju opustit nas system:
     * agregace (medián Kc/m2, pocty, vyvoj) smi vzdy, JEDNOTLIVE TRANSAKCE
     * jen pri souhlasu A pri overeni uzivatele pres eID urovne „znacna".
     * Prepinac sam nestaci — rozhoduje `marketDisclosureDecision`
     * (`@reos/duediligence` → `public/market-disclosure.ts`).
     *
     * Zadani CENY1 ji pise jako `WSDP_SHARING_CONSENT=1`, zbytek repa pouziva
     * `true`/`false` — bere se proto obojí, fail-closed na cokoli jine.
     */
    WSDP_SHARING_CONSENT: z
        .enum(['true', 'false', '1', '0'])
        .default('false')
        .transform((value) => value === 'true' || value === '1'),
    /**
     * Krok LV1 — PLACENE STAZENI VYPISU Z LV NA KLIKNUTI MAKLERE.
     *
     * Vychozi FALSE. Ostre spusteni je az po souhlasu ČÚZK se sirenim udaju
     * katastru (§ 11a odst. 4 vyhl. 358/2013, zadost Axto) — do te doby se
     * tlacitko vubec nekresli a WSDP se z teto cesty NEVOLA.
     *
     * Prepinac SAM NESTACI: rozhoduje `lvPurchaseAllowed` (`@reos/duediligence`
     * → `public/lv-purchase-policy.ts`), ktere k nemu pridava souhlas se
     * sirenim (nebo vyjimku interni testovaci kancelare KNT1) a prostredi
     * WSDP. Jedno misto pravdy, aby zapnuti neznamenalo hledani volajicich.
     */
    LV_PURCHASE_ENABLED: z
        .enum(['true', 'false'])
        .default('false')
        .transform((value) => value === 'true'),
    // ---------------------------------------------------------------- MAIL1
    // KLIENTSKE e-maily (odkaz k podpisu, odkaz na formular, report). Jsou to
    // transakcni zpravy JEDNOMU cloveku, ne kampan — proto maji VLASTNI
    // poskytovatele i VLASTNI pojistku a nesahaji na `REOS_OUTBOUND_SEND`.
    /**
     * Poskytovatel systemoveho odesilatele. `mailpit` je VYCHOZI (dev/preview:
     * zprava skonci v Mailpitu a nikam neodejde), `smtp` je nas mailserver
     * (schranka `noreply@maklerov.cz`), `resend` je volitelna varianta.
     */
    REOS_TX_MAIL_PROVIDER: z.enum(['mailpit', 'smtp', 'resend']).default('mailpit'),
    /** Adresa systemoveho odesilatele (`noreply@maklerov.cz`). Jmeno sklada kod z brandu kancelare. */
    REOS_TX_MAIL_FROM: z.string().min(3).optional(),
    /**
     * POJISTKA PROSTREDI (vzor Pobytnik `assertRealSmtp`): v ostrem prostredi
     * se nastavi `1` a konfigurace mirici na Mailpit/localhost pak NENASTARTUJE.
     * Bez ni by produkce tise posilala klientske e-maily do dev schranky.
     */
    REOS_TX_MAIL_REQUIRE_REAL: z.enum(['0', '1']).default('0'),
    /**
     * HLAVNI POJISTKA klientskych e-mailu. Oddelena od kampani
     * (`REOS_OUTBOUND_SEND`) zamerne: kampane a dopisy klientum se odemykaji
     * v jinou dobu a kazda zmena jedne nesmi odemknout druhou. Pri `off` se
     * NIC neodesle a UI to rekne (nabidne zkopirovani odkazu).
     */
    REOS_CLIENT_MAIL_SEND: z.enum(['off', 'on']).default('off'),
    /** Prihlasovaci udaje schranky systemoveho odesilatele. Jen z prostredi serveru. */
    SMTP_USER: z.string().min(1).optional(),
    SMTP_PASS: z.string().min(1).optional(),
    // ---------------------------------------------------------------- CEE1
    /**
     * Krok CEE1/CEE2: Centralni evidence exekuci (Exekutorska komora CR), API
     * 4.1.0. NENASTAVENO (bez klice a secretu) = lustrace vypnuta a report
     * pise „ověříme v centrální evidenci exekucí před podpisem". Klic a secret
     * oba, nebo zadny. URL je volitelna (vychozi `https://www.ceecr.cz/api/v4`,
     * `CEE_DEFAULT_API_URL` v `@reos/duediligence`).
     */
    CEE_API_URL: z.string().url().optional(),
    /** API klic uctu CEE 4.1.0 (Nastaveni -> API pristupy). Do repa NIKDY - jen env. */
    CEE_API_KEY: z.string().min(1).optional(),
    /** API secret uctu CEE. SECRET: nikdy v logu, DB ani URL mimo klienta CEE. */
    CEE_API_SECRET: z.string().min(1).optional(),
    /**
     * Strop nakladu (R.B. 5. 10. 2026): kolik vlastniku se v cene Kompletu
     * overi SAMO. Vychozi 2 pokryje manzele / SJM; dalsi potvrzuje makler.
     */
    CEE_INCLUDED_OWNERS: z.coerce.number().int().min(0).max(20).default(2),
    /** Za kolik dni se smi znovu pouzit vysledek tehoz subjektu v teze kancelari. */
    CEE_CACHE_DAYS: z.coerce.number().int().min(0).max(90).default(14),
    /**
     * Cenik CEE po pasmech podle poctu dotazu za rok: `do:cena,…,*:cena`
     * (Kc za dotaz). Vychozi = cenik CEE platny 5. 10. 2026.
     */
    CEE_PRICE_TIERS: z
        .string()
        .regex(/^(\d+|\*):\d+(,(\d+|\*):\d+)*$/)
        .default('1000:60,10000:30,1000000:15,*:6'),
    /** Timeout jednoho volani API CEE v ms. */
    CEE_TIMEOUT_MS: z.coerce.number().int().min(1_000).default(20_000),
    // --------------------------------------------------------------- KAMP2
    // Kampaň realitním kancelářím (docs/agent-briefs/step-KAMP2-odesilani.md).
    // Vlastní odesílání REOS — akviziční pipeline RedHand (acq-v3) je vypnutá
    // a nesmí se použít. Klíče PATRÍ jen do prostředí, nikdy do repa.
    /**
     * Poskytovatel odesílání. `dryrun` je VÝCHOZÍ a znamená „slož, ale nikam
     * neposílat“; `mailpit` je náhled na preview (Mailpit na portu 1025),
     * `smtp` je vlastní SMTP a `mailgun` EU API (bod 1 zadání).
     */
    REOS_MAIL_PROVIDER: z.enum(['dryrun', 'mailpit', 'smtp', 'mailgun', 'brevo']).default('dryrun'),
    /**
     * HLAVNÍ POJISTKA (bod 2 zadání): odesílání běží JEN při `on`. V jakémkoli
     * jiném prostředí je `off` a handler vyhodí chybu — stejný vzor jako acq-v3
     * commit 0d74ee0. Nejde o přepínač pohodlí, jde o podmínku, kterou musí
     * R.B. explicitně odemknout až po GO (domény + DNS + texty + vlna 50).
     */
    REOS_OUTBOUND_SEND: z.enum(['off', 'on']).default('off'),
    /** Tajemství — jen z prostředí (SOPS), nikdy do repa ani do logu. */
    REOS_MAILGUN_KEY: z.string().min(1).optional(),
    /**
     * Mailgun EU region (`api.eu.mailgun.net`). US region by posílal data mimo
     * EU a LIA by neplatil, proto je hodnota výchozí a změna je vědomá.
     */
    REOS_MAILGUN_API_BASE: z.string().url().default('https://api.eu.mailgun.net/v3'),
    /**
     * Podepisovací klíč webhooků Mailgunu. Bez něj se webhook NEOBDRŽÍ (bod 4) —
     * nepodepsaný webhook je totiž jen veřejný POST, kterým by kdokoli mohl
     * kanceláři označit za odslouženou nebo ji zařadit do suppression.
     */
    REOS_MAILGUN_WEBHOOK_SIGNING_KEY: z.string().min(1).optional(),
    /** Odesílatelné domény čárkou. Prázdné = žádná doména = nikam to neposlat. */
    REOS_SENDING_DOMAINS: z.string().default(''),
    /** Reply-To schránka pro odpovědi (bod 8 zadání) — musí být doručitelná. */
    REOS_REPLY_TO: z.string().email().optional(),
    /** Jméno konkrétního člověka v `From` (bod 6: odesílatel je konkrétní člověk). */
    REOS_MAIL_SENDER_NAME: z.string().min(1).optional(),
    /** Schránka odesílatele; musí být na `REOS_SENDING_DOMAINS`. */
    REOS_MAIL_SENDER_EMAIL: z.string().email().optional(),
    /** Identifikace provozovatele v patičce (zadání 480/2004 Sb. §7). */
    REOS_OPERATOR_NAME: z.string().min(1).optional(),
    REOS_OPERATOR_ADDRESS: z.string().min(1).optional(),
    /**
     * Veřejný kontakt kanceláře = „proč vám píšeme“ v patičce.
     *
     * MRGD1: ANO, je to druhá proměnná pro „kontakt provozovatele" vedle
     * `REOS_PUBLIC_CONTACT_EMAIL` — ODLIŠNOST JE ZÁMĚRNÁ a nesjednocuje se:
     * tahle je POVINNÁ část patičky obchodního sdělení (480/2004 Sb. §7),
     * takže `campaign:send` bez ní musí SPADNOUT (`SendCliError`), kdežto
     * `REOS_PUBLIC_CONTACT_EMAIL` je kontakt pro subjekty údajů na právních
     * stránkách a v záznamu čl. 30 a bez něj se jen vypíše „doplnit".
     * Sjednocení na jednu proměnnou by jednu z těch dvou povah muselo
     * obětovat. Soupis všech míst je v `REPORT-step-MRGD1.md`.
     */
    REOS_OPERATOR_CONTACT_EMAIL: z.string().email().optional(),
    /** Základ veřejného webu — z něj se skládá `/k/<slug>` a `/odhlaseni/<token>`. */
    REOS_PUBLIC_WEB_BASE_URL: z.string().url().optional(),
    /** Denní strop celé kampaně (plán 50 → 100 → 250 → 500; posun v adminu/env). */
    REOS_CAMPAIGN_DAILY_CAP: z.coerce.number().int().positive().default(50),
    /** Strop na JEDNU odesílací doménu denně — jinak by limit vyprázdnil jednu. */
    REOS_CAMPAIGN_DOMAIN_DAILY_CAP: z.coerce.number().int().positive().default(50),
    /** Pracovní doba odesílání v místním čase (Po–Pá 8–17). */
    REOS_CAMPAIGN_SEND_WINDOW_START_HOUR: z.coerce.number().int().min(0).max(23).default(8),
    REOS_CAMPAIGN_SEND_WINDOW_END_HOUR: z.coerce.number().int().min(1).max(24).default(17),
    /** Náhodný rozestup mezi dvěma e-maily (náhodný odstup, ne fixní cadence). */
    REOS_CAMPAIGN_MIN_GAP_MS: z.coerce.number().int().min(0).default(90_000),
    REOS_CAMPAIGN_MAX_GAP_MS: z.coerce.number().int().min(0).default(600_000),
    /** Automatická brzda (bod 3): bounce % a stížnosti % za den. */
    REOS_CAMPAIGN_BOUNCE_LIMIT_PCT: z.coerce.number().positive().max(100).default(3),
    REOS_CAMPAIGN_COMPLAINT_LIMIT_PCT: z.coerce.number().positive().max(100).default(0.1),
    /** Warm-up (bod 10): dny zahřívání, strop prvního dne a násobek růstu. */
    REOS_CAMPAIGN_WARMUP_DAYS: z.coerce.number().int().min(1).default(6),
    REOS_CAMPAIGN_WARMUP_FIRST_DAY_CAP: z.coerce.number().int().positive().default(10),
    REOS_CAMPAIGN_WARMUP_DAILY_GROWTH: z.coerce.number().min(1).default(2),
    /** Připomínka nejdřív po N pracovních dnech a VŽDY jen jedna (bod 7). */
    REOS_CAMPAIGN_FOLLOWUP_WORKDAYS: z.coerce.number().int().min(3).default(5),
    // --------------------------------------------------------------- KAMP3
    // Brevo (docs/agent-briefs/step-KAMP3-brevo-domeny.md): odesilani vetsiny
    // vlny ze ZAHRATE domeny (redhand.cz, uz overena v Brevo) a zbytek
    // pomalu z NOVE domeny REOS (warm-up plan beze zmeny z KAMP2).
    /** Tajemstvi — jen z prostredi, nikdy do repa ani do logu. */
    REOS_BREVO_KEY: z.string().min(1).optional(),
    /** Transakcni API v3 Brevo — jedine misto, kde je ta adresa v repu. */
    REOS_BREVO_API_BASE: z.string().url().default('https://api.brevo.com/v3'),
    /**
     * Brevo svoje webhooky NEPODEPISUJE (na rozdil od Mailgunu) — je to na
     * nas, jak si webhook URL ochranime. Token se posila jako `?token=` v
     * adrese webhooku nastavene v Brevo adminu a porovnava se konstantne.
     */
    REOS_BREVO_WEBHOOK_TOKEN: z.string().min(1).optional(),
    /**
     * Vlastni denni strop ZAHRATE domeny (bod 2 zadani: "zahrata domena ma
     * vlastni strop, vychozi 300/den"). NENI to totez co
     * `REOS_CAMPAIGN_DOMAIN_DAILY_CAP` — ten dal plati pro NOVOU domenu (strop
     * po doberhnuti warm-up planu), zahrata domena uz zahrivani nema.
     */
    REOS_CAMPAIGN_WARM_DOMAIN_DAILY_CAP: z.coerce.number().int().positive().default(300),
})
    // ------------------------------------------- KAMP2: pravidla mezi promennymi
    // Zbývající kontroly nelze vyjádřit tvarem jednoho pole (a `superRefine` je
    // přesně to místo, kde se to vyplatí říct NAJEDNOU při startu, místo aby se
    // to zjistilo až v půlce dne, když se e-maily zaseknou).
    .superRefine((env, ctx) => {
    const fail = (path, message) => {
        ctx.addIssue({ code: 'custom', path: [path], message });
    };
    // Klíč se nikdy nehádá. `mailgun` bez klíče = chyba při startu.
    if (env.REOS_MAIL_PROVIDER === 'mailgun') {
        if (env.REOS_MAILGUN_KEY === undefined) {
            fail('REOS_MAILGUN_KEY', 'poskytovatel mailgun bez REOS_MAILGUN_KEY — klic je jen v prostredi');
        }
        if (env.REOS_MAILGUN_WEBHOOK_SIGNING_KEY === undefined) {
            fail('REOS_MAILGUN_WEBHOOK_SIGNING_KEY', 'poskytovatel mailgun bez podepisovaciho klice — nepodepsany webhook se neprijme');
        }
    }
    /*
     * Krok COMGATE1: platebni brana se nikdy nehada. `paygw` bez adresy,
     * klice nebo podpisoveho tajemstvi = chyba pri startu, ne objednavka,
     * ktera skonci 503 az ve chvili, kdy chce nekdo zaplatit.
     */
    if ((env.PAYMENT_PROVIDER ?? env.COMMERCE_PAYMENT_PROVIDER) === 'paygw') {
        if (env.PAYGW_URL === undefined) {
            fail('PAYGW_URL', 'poskytovatel paygw bez PAYGW_URL — adresa brany je jen v prostredi');
        }
        if (env.PAYGW_API_KEY === undefined) {
            fail('PAYGW_API_KEY', 'poskytovatel paygw bez PAYGW_API_KEY — klic je jen v prostredi');
        }
        if (env.PAYGW_WEBHOOK_SECRET === undefined) {
            fail('PAYGW_WEBHOOK_SECRET', 'poskytovatel paygw bez podpisoveho tajemstvi — nepodepsany webhook se neprijme');
        }
    }
    // Stejne jako mailgun: klic a webhook token se nikdy neodhaduji.
    if (env.REOS_MAIL_PROVIDER === 'brevo') {
        if (env.REOS_BREVO_KEY === undefined) {
            fail('REOS_BREVO_KEY', 'poskytovatel brevo bez REOS_BREVO_KEY — klic je jen v prostredi');
        }
        if (env.REOS_BREVO_WEBHOOK_TOKEN === undefined) {
            fail('REOS_BREVO_WEBHOOK_TOKEN', 'poskytovatel brevo bez webhook tokenu — Brevo webhooky nepodepisuje, bez tokenu by sel POST podvrhnout');
        }
    }
    // Odesílat bez domény by posílal z cizí/neexistující domény (SPF/DKIM).
    const domains = campaignSendingDomains(env);
    if (env.REOS_MAIL_PROVIDER !== 'dryrun' && env.REOS_MAIL_PROVIDER !== 'mailpit') {
        if (domains.length === 0) {
            fail('REOS_SENDING_DOMAINS', `poskytovatel ${env.REOS_MAIL_PROVIDER} potrebuje alespon jednu REOS_SENDING_DOMAINS`);
        }
        if (env.REOS_MAIL_SENDER_EMAIL === undefined) {
            fail('REOS_MAIL_SENDER_EMAIL', 'odesilatel potrebuje REOS_MAIL_SENDER_EMAIL');
        }
        else if (domains.length > 0 && !domains.includes(emailDomain(env.REOS_MAIL_SENDER_EMAIL))) {
            fail('REOS_MAIL_SENDER_EMAIL', 'odesilatel mimo REOS_SENDING_DOMAINS — z cizi domeny se neposila');
        }
    }
    // Pracovní okno musí dávat smysl (start < konec) a rozestup musí dát náhodný
    // rozestup včetně pauzy (min < max).
    if (env.REOS_CAMPAIGN_SEND_WINDOW_START_HOUR >= env.REOS_CAMPAIGN_SEND_WINDOW_END_HOUR) {
        fail('REOS_CAMPAIGN_SEND_WINDOW_END_HOUR', 'konec odesilaciho okna musi byt po startu (Po-Pa 8-17)');
    }
    if (env.REOS_CAMPAIGN_MIN_GAP_MS >= env.REOS_CAMPAIGN_MAX_GAP_MS) {
        fail('REOS_CAMPAIGN_MAX_GAP_MS', 'maximalni rozestup musi byt vetsi nez minimalni');
    }
    // Doménový strop vyšší než denní by „limitoval“ něco, co se nestihne ani
    // celé — a vypadal by jako pojistka, která nefunguje.
    if (env.REOS_CAMPAIGN_DOMAIN_DAILY_CAP > env.REOS_CAMPAIGN_DAILY_CAP) {
        fail('REOS_CAMPAIGN_DOMAIN_DAILY_CAP', 'doménový strop nemůže být vyšší než denní (jinak není limitem)');
    }
    // ------------------------------------------------ AKV1: akvizicni radar
    // Napul nastavene napojeni (URL bez tokenu nebo naopak) je preklep, ne
    // vypnuta funkce — radeji nenastartovat nez tise ukazovat „Připravujeme".
    if ((env.AKV_RADAR_URL === undefined) !== (env.AKV_REOS_SERVICE_TOKEN === undefined)) {
        fail(env.AKV_RADAR_URL === undefined ? 'AKV_RADAR_URL' : 'AKV_REOS_SERVICE_TOKEN', 'AKV_RADAR_URL a AKV_REOS_SERVICE_TOKEN se nastavuji spolu (napojeni akvizicniho radaru)');
    }
    // ------------------------------------------------- MAIL1: klientske maily
    // Pojistka prostredi (vzor Pobytnik `assertRealSmtp`): s
    // `REOS_TX_MAIL_REQUIRE_REAL=1` se instance NESPUSTI, kdyz systemovy
    // odesilatel miri do dev schranky. Tise posilat dopisy klientum do Mailpitu
    // je horsi nez nenastartovat.
    if (env.REOS_TX_MAIL_REQUIRE_REAL === '1') {
        const sink = txMailDevSinkReason(env);
        if (sink !== null) {
            fail('REOS_TX_MAIL_PROVIDER', `REOS_TX_MAIL_REQUIRE_REAL=1: systemovy odesilatel miri na dev cil (${sink}) — odmitam start`);
        }
        if (env.REOS_TX_MAIL_FROM === undefined) {
            fail('REOS_TX_MAIL_FROM', 'ostry systemovy odesilatel potrebuje REOS_TX_MAIL_FROM');
        }
    }
    if (env.REOS_TX_MAIL_PROVIDER === 'resend' && env.RESEND_API_KEY === undefined) {
        fail('RESEND_API_KEY', 'REOS_TX_MAIL_PROVIDER=resend bez RESEND_API_KEY — klic je jen v prostredi');
    }
    // ----------------------------------------------- SMS1: android-gateway
    // Přihlašovací údaje se nikdy neodhadují (stejné pravidlo jako Mailgun/
    // Brevo výš) a local mode nesmí tiše spadnout na veřejný cloud relay -
    // adresa telefonu/tunelu je pro každou instalaci jiná.
    if (env.SMS_PROVIDER === 'android-gateway') {
        if (env.SMS_GATEWAY_USER === undefined || env.SMS_GATEWAY_PASSWORD === undefined) {
            fail('SMS_GATEWAY_USER', 'SMS_PROVIDER=android-gateway bez SMS_GATEWAY_USER/SMS_GATEWAY_PASSWORD — přihlašovací údaje z telefonu jsou jen v prostředí');
        }
        if (env.SMS_GATEWAY_MODE === 'local' &&
            env.SMS_GATEWAY_URL === 'https://api.sms-gate.app/3rdparty/v1') {
            fail('SMS_GATEWAY_URL', 'SMS_GATEWAY_MODE=local potřebuje vlastní SMS_GATEWAY_URL (adresu telefonu/tunelu) — veřejný cloud relay by brance nerozuměl, kam poslat SMS');
        }
    }
    // ------------------------------------- E2E5: predvolby cisel do stubu SMS
    // Predvolba, ktera nema tvar E.164, by nikdy nesedla a pojistka by TICHE
    // nefungovala — presne ten druh selhani, kvuli kteremu krok vznikl.
    for (const prefix of splitList(env.SMS_STUB_RECIPIENT_PREFIXES)) {
        if (!/^\+\d{2,14}$/.test(prefix)) {
            fail('SMS_STUB_RECIPIENT_PREFIXES', `předvolba „${prefix}" není v E.164 (čeká se „+" a číslice, např. +420999) — takhle zapsaná by na žádné číslo nesedla`);
        }
    }
    // ------------------------- P6A: povolena cisla pilotu (skutecna SMS)
    // Cislo, ktere nema tvar E.164, by v seznamu NIKDY nesedlo a pilot by
    // tise nic neodesilal (`allowlist` je fail-closed) — tedy presne ta
    // trida selhani, kvuli ktere se hodnota kontroluje uz pri startu.
    for (const raw of splitList(env.SMS_ALLOWED_RECIPIENTS)) {
        if (!/^\+\d{8,15}$/.test(normalizeSmsRecipient(raw))) {
            fail('SMS_ALLOWED_RECIPIENTS', `číslo „${raw}" není v E.164 (čeká se „+" a 8–15 číslic, např. +420777123456) — takhle zapsané by v seznamu nikdy nesedlo`);
        }
    }
    // ------------------- MAIL2: povolene adresy ostre posty (jen nase domena)
    // Tataz uvaha jako u cisel vyse: zaznam, ktery neni ani cela adresa, ani
    // domena `@firma.cz`, by v seznamu NIKDY nesedl a `allowlist` by tise
    // neposlal nic — porucha, kterou by nikdo nepoznal od „pojistka funguje".
    for (const raw of splitList(env.MAIL_ALLOWED_RECIPIENTS)) {
        if (mailAllowlistEntryError(raw) !== null) {
            fail('MAIL_ALLOWED_RECIPIENTS', `zápis „${raw}" ${mailAllowlistEntryError(raw)} — čeká se celá adresa (info@redhand.cz) nebo domena (@redhand.cz)`);
        }
    }
    // Zachytavaci SMTP MUSI byt mistni schranka. Relay do internetu by znamenal
    // „zablokovanou zpravu posli stejne ven", tedy pojistku naruby.
    if (env.MAIL_CATCH_URL !== undefined && !isDevMailSinkTarget(env.MAIL_CATCH_URL)) {
        fail('MAIL_CATCH_URL', `„${env.MAIL_CATCH_URL}" není místní zachytávací schránka (mailpit/localhost) — zablokovaná zpráva by odešla ven`);
    }
    // ---------------------------------------- DEMO7: verejne demo bez hesla
    // Jedna pravda (`publicDemoEnvViolations`), dve pouziti: tady pri startu a
    // v testech. Kdyby se kontrola psala rovnou sem, nemel by ji jak zavolat
    // nikdo jiny — a prvni, kdo by na ni zapomnel, by pustil verejne demo s
    // zivou SMS branou.
    for (const violation of publicDemoEnvViolations(env)) {
        fail(violation.path, violation.message);
    }
})
    // `OUTBOUND_SEND` je ODVOZENÁ hodnota: jediný zdroj pravdy je
    // `REOS_OUTBOUND_SEND` a zbytek systému (handler, admin, hlášení) čte jen
    // ji, aby se nikde nesmelo objevit `=== 'on'` s jiným významem.
    .transform((env) => ({
    ...env,
    OUTBOUND_SEND: env.REOS_OUTBOUND_SEND === 'on',
    // MAIL1: tataz uvaha jako u `OUTBOUND_SEND`, ale pro KLIENTSKE e-maily.
    // Dve oddelene pojistky, dve oddelene odvozene hodnoty — kdyby cetly jednu,
    // odemknuti kampani by odemklo i dopisy klientum.
    CLIENT_MAIL_SEND: env.REOS_CLIENT_MAIL_SEND === 'on',
    // E2E5: tyz vzor — rozebrany seznam je JEDNA odvozena hodnota, aby se
    // `split(',')` neopakoval na kazdem miste, kde se predvolby ctou.
    SMS_STUB_PREFIXES: splitList(env.SMS_STUB_RECIPIENT_PREFIXES),
    // P6A: seznam povolenych cisel je ulozen JIZ NORMALIZOVANY, aby se
    // porovnani („je toto cislo v seznamu?") nikdy nedelalo nad surovym
    // zapisem z `.env` — jinak by „+420 777 …" v env a „+420777…" z aplikace
    // byla dve rozdilna cisla a pilot by mlcel.
    SMS_ALLOWED_NUMBERS: splitList(env.SMS_ALLOWED_RECIPIENTS).map(normalizeSmsRecipient),
    // DEMO7: tyz vzor jako `OUTBOUND_SEND` — zbytek systemu se pta na
    // `PUBLIC_DEMO`, nikdy na `=== '1'`.
    PUBLIC_DEMO: env.REOS_PUBLIC_DEMO === '1',
}));
/**
 * Krok DEMO7 bod 1 — CO SE NESMI SEJIT S `REOS_PUBLIC_DEMO=1`.
 *
 * Verejne demo klika kdokoli z internetu. Kdyby na nem byla nastavena ziva
 * brana (SMS, SMTP, Mailgun, Brevo), prvni navstevnik by poslal zpravu
 * skutecnemu cloveku na nase penize. Proto se takova kombinace NEVYHODNOCUJE
 * za behu — proces s ni NENASTARTUJE (tyz vzor jako `REOS_TX_MAIL_REQUIRE_REAL`
 * z MAIL1, jen obracene: tam se vynucuje ostry cil, tady atrapa).
 *
 * Prazdny seznam = konfigurace je pro verejne demo pripustna. Kdyz
 * `REOS_PUBLIC_DEMO=0`, nekontroluje se nic (bezna instance).
 */
export function publicDemoEnvViolations(env) {
    if (env.REOS_PUBLIC_DEMO !== '1')
        return [];
    const out = [];
    const fail = (path, message) => {
        out.push({ path, message });
    };
    if (env.REOS_PUBLIC_DEMO_EMAIL === undefined) {
        fail('REOS_PUBLIC_DEMO_EMAIL', 'verejne demo bez REOS_PUBLIC_DEMO_EMAIL — brana prihlaseni by nemela s cim porovnavat a pustila by kazdy ucet');
    }
    if (env.SMS_PROVIDER !== 'stub') {
        fail('SMS_PROVIDER', `verejne demo smi jen SMS_PROVIDER=stub (je ${env.SMS_PROVIDER}) — SMS z ukazky by sla skutecnemu cloveku na nase penize`);
    }
    if (env.REOS_MAIL_PROVIDER !== 'dryrun' && env.REOS_MAIL_PROVIDER !== 'mailpit') {
        fail('REOS_MAIL_PROVIDER', `verejne demo smi jen dryrun/mailpit (je ${env.REOS_MAIL_PROVIDER}) — kampanovy poskytovatel odesila skutecne`);
    }
    if (env.REOS_TX_MAIL_PROVIDER !== 'mailpit') {
        fail('REOS_TX_MAIL_PROVIDER', `verejne demo smi jen REOS_TX_MAIL_PROVIDER=mailpit (je ${env.REOS_TX_MAIL_PROVIDER}) — klientske e-maily by odesly`);
    }
    if (env.REOS_OUTBOUND_SEND !== 'off') {
        fail('REOS_OUTBOUND_SEND', 'verejne demo vyzaduje REOS_OUTBOUND_SEND=off');
    }
    if (env.REOS_CLIENT_MAIL_SEND !== 'off') {
        fail('REOS_CLIENT_MAIL_SEND', 'verejne demo vyzaduje REOS_CLIENT_MAIL_SEND=off');
    }
    return out;
}
/** Seznam oddeleny carkou -> pole bez prazdnych polozek a bez mezer. */
function splitList(raw) {
    return raw
        .split(',')
        .map((item) => item.trim())
        .filter((item) => item !== '');
}
/** Vychozí EU API Mailgunu (jediné místo, kde je tato adresa v repu). */
export const MAILGUN_EU_API_BASE = 'https://api.eu.mailgun.net/v3';
/**
 * KOMU VERIME `X-Forwarded-For` (krok UXP1, nalez P2-15 — bezpecnostni cast).
 *
 * Do UXP1 mely vsechny Fastify aplikace `trustProxy: true`. To znamena „veri
 * hlavicce od kohokoli": `proxy-addr` pak bere NEJLEVEJSI polozku `XFF`, a tu
 * posila klient. `req.ip` byl tedy plne v rukou volajiciho a s nim i
 *   - omezeni prihlaseni na povolene IP (`checkIpAllowlistForEmail`),
 *   - zamek po neuspesnych prihlasenich (`isLoginLocked(email, ip)`),
 *   - IP v `audit_events`.
 * Stacilo poslat `X-Forwarded-For: <povolena IP>`.
 *
 * Verime proto jen smyckam a privatnim rozsahum — tam stoji nase nginx
 * (`proxy_pass http://127.0.0.1:…`) i Caddy v compose (most `app`, 172.16/12).
 * `proxy-addr` pak vraci nejpravejsi adresu MIMO tyhle rozsahy, tedy skutecneho
 * klienta; hlavicka podvrzena klientem uz na vysledek nema vliv.
 *
 * Jedno misto pro vsechny aplikace (api, portal-api, ai-gateway, mcp-gateway) —
 * dve kopie seznamu by se rozesly a jedna by zustala fail-open.
 */
export const TRUSTED_PROXY_RANGES = ['loopback', 'linklocal', 'uniquelocal'];
/** Doména části e-mailu (malá písmena) nebo prázdný řetězec, když tvar nesedí. */
function emailDomain(email) {
    const at = email.lastIndexOf('@');
    return at < 0
        ? ''
        : email
            .slice(at + 1)
            .trim()
            .toLowerCase();
}
/**
 * Pojistka odesílání jako boolean. `true` JEN při `REOS_OUTBOUND_SEND=on` —
 * todle funkce je jediné místo, kde se ta podmínka vyhodnocuje.
 */
export function resolveOutboundSend(env) {
    return env.OUTBOUND_SEND;
}
/**
 * Krok MAIL1 — pojistka odesilani KLIENTSKYCH e-mailu (odkaz k podpisu, odkaz
 * na formular, report). `true` JEN pri `REOS_CLIENT_MAIL_SEND=on`. Zamerne
 * NEcte `OUTBOUND_SEND`: kampane a dopisy klientum se odemykaji zvlast.
 */
export function resolveClientMailSend(env) {
    return env.CLIENT_MAIL_SEND;
}
/**
 * Krok E2E5 bod 1 — TESTOVACÍ PŘÍJEMCE SMS. `true` = zpráva na tohle číslo
 * nesmí opustit server a musí skončit ve stubu, i když je nastavená živá
 * brána. JEDINÉ místo, kde se ta podmínka vyhodnocuje (rozhoduje o tom
 * `StubRoutedSmsProvider`, hostitel se podle téhož výsledku rozhoduje, jestli
 * otevřít okénko do mailpitu).
 *
 * Číslo i předvolba se porovnávají BEZ mezer a oddělovačů: makléř i e2e sada
 * zapisují „+420 999 …" stejně legitimně jako „+420999…" a pojistka, která by
 * si toho nevšimla, by mlčky propustila zprávu do brány.
 */
export function isSmsStubRecipient(to, prefixes) {
    if (prefixes === undefined || prefixes.length === 0)
        return false;
    const number = compactPhone(to);
    if (number === '')
        return false;
    return prefixes.some((prefix) => {
        const compact = compactPhone(prefix);
        return compact !== '' && number.startsWith(compact);
    });
}
/** Telefon jen jako `+` a cislice — mezery, pomlcky a zavorky pryc. */
function compactPhone(raw) {
    return raw.replace(/[^+0-9]/g, '');
}
/**
 * Krok P6A — JEDEN tvar telefonu pro porovnavani s povolenym seznamem.
 *
 * Normalizuje stejne vstup z `.env` i cislo prichozi z aplikace: mezery
 * a oddelovace pryc, mezinarodni predvolba `00…` na `+…`. ZAMERNE nevaliduje
 * (to dela `loadEnv` u hodnoty z prostredi) a NEhada zemi — `@reos/config`
 * nesmi zavist na `libphonenumber-js`; plnou validaci telefonu dela
 * `normalizePhone` v `@reos/party` tam, kde se cislo POPRVE uklada.
 */
export function normalizeSmsRecipient(raw) {
    const compact = compactPhone(raw);
    return compact.startsWith('00') ? `+${compact.slice(2)}` : compact;
}
/**
 * Krok P6A — `true` = cislo JE v seznamu povolenych prijemcu pilotu.
 *
 * JEDINE misto, kde se ta podminka vyhodnocuje (volá ji `StubRoutedSmsProvider`
 * — tedy poskytovatel, pod kterym lezi vsechny cesty: podpis, klientsky
 * formular, CRM). Prazdny/chybejici seznam = `false`, protoze pri politice
 * `allowlist` je spravna odpoved na „nevim" NEODESLAT.
 */
export function isSmsAllowedRecipient(to, allowed) {
    if (allowed === undefined || allowed.length === 0)
        return false;
    const number = normalizeSmsRecipient(to);
    if (number === '')
        return false;
    return allowed.some((entry) => normalizeSmsRecipient(entry) === number);
}
/**
 * Krok P6A — telefon do LOGU. Nikdy cele cislo: zablokovany pokus se musi dat
 * dohledat (makler hlasi „SMS nedosla"), ale log neni misto pro kontakt
 * klienta. `***` + posledni tri cislice staci k identifikaci a neni to PII,
 * ktere by stacilo k volani.
 */
export function maskPhoneForLog(raw) {
    const digits = compactPhone(raw).replace(/\D/g, '');
    return `***${digits.slice(-3)}`;
}
/**
 * Krok MAIL1 — JEDINE misto, kde se pozna „tohle je dev schranka".
 * Vraci duvod (pro hlaseni) nebo `null`, kdyz cil dev neni. Vzor je
 * `assertRealSmtp` z Pobytniku (`email.ts`), rozsireny o `SMTP_URL`, protoze
 * REOS SMTP cil zapisuje i jednou adresou.
 */
export function txMailDevSinkReason(env) {
    if (env.REOS_TX_MAIL_PROVIDER === 'mailpit')
        return 'provider=mailpit';
    const host = (env.SMTP_HOST ?? '').trim().toLowerCase();
    const url = (env.SMTP_URL ?? '').trim().toLowerCase();
    const target = url !== '' ? url : host;
    if (target === '')
        return null;
    if (isDevMailSinkTarget(target)) {
        return `host=${target}`;
    }
    // Mailpit SMTP posloucha na 1025; port je stejne silny signal jako jmeno.
    if (env.SMTP_PORT === 1025 || /:1025(\/|$)/.test(url))
        return 'port=1025';
    return null;
}
/**
 * Krok QAFIX8 — `true` = tahle adresa je DEV SCHRANKA (mailpit/mailhog na
 * mistnim stroji), ne relay do internetu. JEDINE misto, kde se to pozna
 * (`txMailDevSinkReason` i `resolveOutboundMailTransport` ctou tohle).
 */
export function isDevMailSinkTarget(target) {
    return /(^|[/@])(localhost|127\.0\.0\.1|\[::1\]|::1|mailpit|mailhog)(:|\/|$)/.test(target.trim().toLowerCase());
}
/**
 * Krok MAIL2 — JEDEN tvar adresy pro porovnavani s povolenym seznamem.
 *
 * Zpravy nesou adresu i ve tvaru `Jan Novák <Info@Redhand.CZ>` (to je legalni
 * `To`), `.env` ji pise rucne. Pojistka, ktera by porovnavala surovy text, by
 * `Info@Redhand.CZ` v seznamu nenasla a zpravu pro NASI schranku zablokovala —
 * a naopak u nepovolene adresy v lomenych zavorkach by videla jen jmeno.
 */
export function normalizeMailRecipient(raw) {
    const angle = /<([^>]*)>/.exec(raw);
    return (angle?.[1] ?? raw).trim().toLowerCase();
}
/**
 * Krok MAIL2 — co je na zapisu v `MAIL_ALLOWED_RECIPIENTS` spatne (`null` = je
 * v poradku). Povolene tvary: cela adresa `info@redhand.cz` nebo domena
 * `@redhand.cz`. JEDINA pravda o tvaru — cte ji kontrola pri startu
 * i `isMailAllowedRecipient`.
 */
export function mailAllowlistEntryError(raw) {
    const entry = normalizeMailRecipient(raw);
    if (entry === '')
        return 'je prázdný';
    if (entry.includes(' '))
        return 'obsahuje mezeru';
    const at = entry.indexOf('@');
    if (at === -1)
        return 'není adresa ani domena (chybí „@")';
    if (entry.indexOf('@', at + 1) !== -1)
        return 'obsahuje „@" dvakrát';
    const domain = entry.slice(at + 1);
    if (!/^[a-z0-9.-]+\.[a-z]{2,}$/.test(domain))
        return 'nemá platnou domenu';
    return null;
}
/**
 * Krok MAIL2 — pravidlo z prostredi. JEDINE misto, kde se cte
 * `MAIL_RECIPIENT_POLICY` a `MAIL_ALLOWED_RECIPIENTS`.
 *
 * Chybejici promenne = `allowlist` s prazdnym seznamem, tedy ostre neodejde
 * NIC: narocna cast prostredi (relay s heslem) se zapomenout da, pojistka se
 * zapomenout NESMI.
 *
 * ROZDIL PROTI SMS (zamerny): seznam se rozebira pri kazdem cteni ze SUROVE
 * promenne, zadne odvozene pole v `Env` jako `SMS_ALLOWED_NUMBERS`. Postu
 * posilaji i procesy, ktere dostavaji ZUZENY `OutboundMailEnv` (napr. okenko
 * do stubovane SMS), a odvozene pole by jim chybelo — druha pravda o tom,
 * kdo je povoleny, je presne to, co se rozejde.
 */
export function mailRecipientGuard(env) {
    return {
        policy: env.MAIL_RECIPIENT_POLICY ?? 'allowlist',
        allowed: splitList(env.MAIL_ALLOWED_RECIPIENTS ?? '').filter((entry) => mailAllowlistEntryError(entry) === null),
    };
}
/**
 * Krok MAIL2 — `true` = adresa JE povolena. Prazdny/chybejici seznam = `false`,
 * protoze pri politice `allowlist` je spravna odpoved na „nevim" NEPOSILAT.
 */
export function isMailAllowedRecipient(address, allowed) {
    if (allowed === undefined || allowed.length === 0)
        return false;
    const value = normalizeMailRecipient(address);
    if (value === '' || !value.includes('@'))
        return false;
    const domain = value.slice(value.indexOf('@'));
    return allowed.some((raw) => {
        const entry = normalizeMailRecipient(raw);
        // Zaznam zacinajici `@` je DOMENA (plati i pro `info+test@redhand.cz`),
        // jinak se cela adresa musi rovnat.
        return entry.startsWith('@') ? domain === entry : value === entry;
    });
}
/**
 * Krok MAIL2 — prijemci, kteri pojistkou NEPROSLI (prazdne pole = zprava smi
 * odejit ostre).
 *
 * Rozhoduje se nad VSEMI prijemci zpravy (to, cc, bcc) naraz, protoze jedno
 * SMTP spojeni je jedna zprava: castecne doruceni by znamenalo poslat text
 * urceny klientovi na adresu, kterou nikdo nepovolil, jen bez teto jedne
 * polozky v `To`.
 *
 * ZAMERNE `!== 'open'`, ne `=== 'allowlist'` (tyz duvod jako u SMS): branu smi
 * otevrit jen VYSLOVNE vyjadrene `open`, aby poskozeny guard — preklep v env,
 * chybejici pole po merge — neznamenal TICHE otevreni brany cizim adresam.
 */
export function blockedMailRecipients(recipients, guard) {
    if (guard.policy === 'open')
        return [];
    return recipients.filter((address) => !isMailAllowedRecipient(address, guard.allowed));
}
/**
 * Krok QAFIX8 bod 1b — pojistka podle druhu posty. Chybejici hodnota = ZAVRENO
 * (fail closed): „nevim, jestli se smi posilat" nesmi znamenat „posli".
 */
export function outboundMailGate(env, kind) {
    switch (kind) {
        case 'client':
            return env.CLIENT_MAIL_SEND === true
                ? { allowed: true, gate: 'REOS_CLIENT_MAIL_SEND', reason: null }
                : { allowed: false, gate: 'REOS_CLIENT_MAIL_SEND', reason: 'client_mail_send_off' };
        case 'campaign':
            return env.OUTBOUND_SEND === true
                ? { allowed: true, gate: 'REOS_OUTBOUND_SEND', reason: null }
                : { allowed: false, gate: 'REOS_OUTBOUND_SEND', reason: 'outbound_send_off' };
        case 'utility':
            return env.UTILITY_AUTO_SEND === true
                ? { allowed: true, gate: 'UTILITY_AUTO_SEND', reason: null }
                : { allowed: false, gate: 'UTILITY_AUTO_SEND', reason: 'utility_auto_send_off' };
        case 'system':
            return { allowed: true, gate: 'system', reason: null };
    }
}
/** Vychozi SMTP port mailpitu, kdyz prostredi zadnou mistni schranku nezna. */
const MAILPIT_DEFAULT_PORT = 1025;
/**
 * Krok QAFIX8 bod 1a — CIL odchozi posty. V rezimu `mailpit` (kterakoli
 * z promennych poskytovatele) se `SMTP_URL` IGNORUJE, pokud nemiri na mistni
 * schranku: na preview byl relay ven zapsany v `SMTP_URL` a kazda cesta, ktera
 * si transport skladala sama, ho poslusne pouzila.
 *
 * `null` = prostredi nestaci (zadny host ani URL) a volajici z toho MUSI udelat
 * „odesilatel nenastaven", nikdy tichy no-op.
 */
export function resolveOutboundMailTransport(env) {
    const url = env.SMTP_URL?.trim() ?? '';
    const host = env.SMTP_HOST?.trim() ?? '';
    const mailpitMode = env.REOS_MAIL_PROVIDER === 'mailpit' || env.REOS_TX_MAIL_PROVIDER === 'mailpit';
    if (mailpitMode) {
        // Mailpit nema prihlasovani ani TLS. Poradi: mistni host z prostredi →
        // mistni `SMTP_URL` → vychozi `localhost:1025`.
        if (host !== '' && isDevMailSinkTarget(host)) {
            return {
                provider: 'mailpit',
                host,
                port: env.SMTP_PORT ?? MAILPIT_DEFAULT_PORT,
                secure: false,
                requireTLS: false,
            };
        }
        if (url !== '' && isDevMailSinkTarget(url)) {
            return { provider: 'mailpit', url, secure: false, requireTLS: false };
        }
        return {
            provider: 'mailpit',
            host: 'localhost',
            port: MAILPIT_DEFAULT_PORT,
            secure: false,
            requireTLS: false,
        };
    }
    if (url === '' && host === '')
        return null;
    /**
     * OPRAVA 1 kroku QAFIX8 — MISTNI SCHRANKA SE POZNA I BEZ `REOS_MAIL_PROVIDER`.
     *
     * `requireTLS` a prihlaseni patri RELAY do internetu, ne mailpitu: ten TLS
     * ani AUTH neinzeruje a nodemailer na nem skonci `ESOCKET`. Prvni beh kroku
     * to nerozlisoval a tim shodil kazdou cestu, ktera jde na host+port mimo
     * rezim mailpit — treba okenko do stubovane SMS (`apps/api/src/lib/
     * sms-stub-window.ts`), ktere `SMTP_URL` ZAMERNE nedostava.
     */
    if (isDevMailSinkTarget(url !== '' ? url : host)) {
        return url !== ''
            ? { provider: 'mailpit', url, secure: false, requireTLS: false }
            : {
                provider: 'mailpit',
                host,
                port: env.SMTP_PORT ?? MAILPIT_DEFAULT_PORT,
                secure: false,
                requireTLS: false,
            };
    }
    const port = env.SMTP_PORT ?? 587;
    const user = env.SMTP_USER?.trim() ?? '';
    return {
        provider: 'smtp',
        ...(url !== '' ? { url } : { host, port }),
        secure: url === '' && port === 465,
        // Nas mailserver chce prihlaseni; `requireTLS` zajisti STARTTLS na 587 —
        // heslo schranky `noreply@maklerov.cz` nesmi jit po nesifrovanem spojeni.
        requireTLS: url === '' && port !== 465,
        ...(user !== '' ? { auth: { user, pass: env.SMTP_PASS ?? '' } } : {}),
    };
}
/**
 * Krok MAIL2 — CIL ZACHYTAVACI SCHRANKY (`MAIL_CATCH_URL`, dnes mailpit).
 *
 * Zprava, kterou pojistka nepustila ven, se nezahodi — jde sem, aby se dalo
 * overit, CO by bylo odeslo. Bez promenne mistni mailpit, protoze „nemame kam
 * zachytavat" nesmi znamenat „tak to posli adresatovi".
 *
 * Tvar se NESKLADA znova: vraci se tentyz `OutboundMailTransport`, jaky dela
 * `resolveOutboundMailTransport` pro rezim mailpit (bez AUTH a TLS — mailpit
 * ani jedno neinzeruje).
 */
export function resolveMailCatchTransport(env) {
    const url = env.MAIL_CATCH_URL?.trim() ?? '';
    if (url !== '' && isDevMailSinkTarget(url)) {
        return { provider: 'mailpit', url, secure: false, requireTLS: false };
    }
    return {
        provider: 'mailpit',
        host: 'localhost',
        port: MAILPIT_DEFAULT_PORT,
        secure: false,
        requireTLS: false,
    };
}
/**
 * Krok QAFIX8 bod 1c — adresat do LOGU. Jen DOMENA: „komu to odeslo" musi byt
 * dohledatelne, ale cela adresa je osobni udaj (CLAUDE.md, PII se neloguje).
 */
export function mailLogDomain(to) {
    const at = to.lastIndexOf('@');
    return at < 0 ? 'neznama' : to.slice(at + 1).trim().toLowerCase();
}
/**
 * Krok QAFIX8 bod 2 (V9) — ZAKLAD ODKAZU V POSTE. `PUBLIC_APP_URL` ma prednost
 * pred `WEB_ADMIN_URL`: preview domena je za basic auth a pozvany se na ni
 * neprihlasi.
 */
export function mailLinkBaseUrl(env) {
    const publicUrl = env.PUBLIC_APP_URL?.trim() ?? '';
    if (publicUrl !== '')
        return publicUrl;
    const admin = env.WEB_ADMIN_URL?.trim() ?? '';
    return admin === '' ? null : admin;
}
/**
 * Krok QAFIX8 bod 2 (V9) — prepis ORIGINU odkazu, ktery slozil nekdo jiny
 * (Better Auth stavi odkaz na reset hesla a overeni e-mailu ze sveho
 * `baseURL`). Cesta i parametry zustavaji; bez `PUBLIC_APP_URL` se nemeni nic.
 */
export function toPublicAppLink(url, env) {
    const publicUrl = env.PUBLIC_APP_URL?.trim() ?? '';
    if (publicUrl === '')
        return url;
    try {
        const target = new URL(url);
        const base = new URL(publicUrl);
        target.protocol = base.protocol;
        target.host = base.host;
        return target.toString();
    }
    catch {
        // Neplatna URL se nepodstrkava dal prepsana — radsi puvodni hodnota.
        return url;
    }
}
/**
 * Krok MRGS1 bod 3 — výjimky z SSRF ochrany schránky makléře: malá písmena,
 * bez prázdných položek, bez duplicit. Prázdný seznam = žádná výjimka.
 */
export function brokerInboxAllowedHosts(env) {
    const out = [];
    for (const raw of env.BROKER_INBOX_ALLOWED_HOSTS.split(',')) {
        const host = raw.trim().toLowerCase();
        if (host === '' || out.includes(host))
            continue;
        out.push(host);
    }
    return out;
}
/** Seznam odesílatelných domén: malá písmena, bez prázdných položek, bez duplicit. */
export function campaignSendingDomains(env) {
    const out = [];
    for (const raw of env.REOS_SENDING_DOMAINS.split(',')) {
        const domain = raw.trim().toLowerCase().replace(/^@/, '');
        if (domain === '' || out.includes(domain))
            continue;
        out.push(domain);
    }
    return out;
}
/**
 * Doma, na kterou se zrovna pise (prvni z `REOS_SENDING_DOMAINS`), a jen kdyz
 * je vypnuta pojistka? NE — vyber domeny je cisty prepinac, bezpecnost ridi
 * `resolveOutboundSend`. Tady jde o to, aby se zbytek systemu nemusel ptát,
 * jestli je vúbec kam psát.
 */
export function primarySendingDomain(env) {
    return campaignSendingDomains(env)[0] ?? null;
}
export function loadEnv(source = process.env) {
    const parsed = envSchema.safeParse(source);
    if (!parsed.success) {
        const detail = parsed.error.issues.map((i) => `  ${i.path.join('.')}: ${i.message}`).join('\n');
        throw new Error(`Neplatna konfigurace prostredi:\n${detail}`);
    }
    return parsed.data;
}
/**
 * Minimalni sirka pristupu k pozemku [m] (krok G2). Jedna hodnota pro cely
 * system, aby si ji zadny beh nedomyslel: vjezd pro osobni auto / prijezd
 * k rodinnemu domu. Neni to pravni norma (ta je vec jurisdikce), je to
 * VYCHOZI pozadavek receptu `accessCorridor`, ktery lze prebit vstupem.
 */
export const ACCESS_MIN_WIDTH_M = 3.0;
/**
 * Tolerance pro overeni vymery Z KATASTRU proti tvaru parcely [%] (krok K0d
 * bod 5). Volnejsi nez u rucniho udaje (`DEFAULT_AREA_TOLERANCE_PCT` = 2 %
 * v `@reos/duediligence`), protoze obe cisla pochazeji z teze sluzby
 * a rozchazeji se jen zaokrouhlenim hranic — Verido na 38/13 ukazuje
 * 1 731 m² dle zapisu proti 1 726 m² z geometrie (0,3 %) a hlasi shodu.
 *
 * TADY, a ne v kontextu `duediligence`, kvuli nalezu N4 kroku MRGR1: tutez
 * mez potrebuje BEH (`compareAreas` v `reconcile.ts`), REPORT (kapitola
 * Pozemek i puntik otisku) a PANEL PARCELY v prohlizeci
 * (`web-admin/src/lib/parcel-panel-rows.ts`). Panel mel vlastni cislo 2 %,
 * takze parcela s rozdilem 2,5 % byla v panelu „nesedi" a v reportu „sedi".
 * Z `@reos/duediligence` si ho vzit nemuze (jediny vstupni bod balicku tahne
 * server-only kod — `test/browser-safe-imports.spec.ts`), zato
 * `@reos/config` uz prohlizecovy bundle bezpecne pouziva; vzor je
 * `ACCESS_MIN_WIDTH_M` vyse.
 */
export const KN_GEOMETRY_TOLERANCE_PCT = 3;
/**
 * Krok RS6 — MEZ, NAD KTEROU ROZDIL VYMER UZ NENI „STARA MAPA" [%].
 *
 * Dve meze, ne jedna, protoze rozdil zapisu proti tvaru parcely ma dve ruzne
 * ctene urovne:
 *
 * - nad `KN_GEOMETRY_TOLERANCE_PCT` (3 %) rozdil UZ NENI zaokrouhleni hranic,
 *   takze ho report pojmenuje vetou „uredni vymera X, podle mapy priblizne Y";
 * - nad `KN_AREA_CONFLICT_PCT` je rozdil tak velky, ze nejpravdepodobnejsi
 *   vysvetleni je JINA PARCELA (nebo zanikly stav), a to uz musi videt clovek
 *   — teprve tady vznika CONFLICT fakt a blokujici otazka.
 *
 * Duvod pro 25 %: mereni 26. 10. 2026 (dum Petrovice u Chabarovic) stavi
 * digitalizovanou mapu proti zapisu — st. 597 má 176 m² v zapisu a 156,3 m²
 * z tvaru (−11,2 %), 1904/12 348 proti 365,1 m² (+4,9 %). U map z pozemkoveho
 * katastru je to bezne a rozhoduje VZDY zapis; hlasit to jako rozpor znamena
 * zastavit report kvuli udaji, ktery nikdo nezmeni. Neni to cislo z vyhlasky,
 * je to provozni mez (stejne jako 3 % vyse) — oba prahy ZIJI TADY, aby
 * prohlizec, beh i report merily jednim meridlem (nalez N4 kroku MRGR1).
 */
export const KN_AREA_CONFLICT_PCT = 25;
export const systemClock = {
    now: () => new Date(Date.now()),
};
// ------------------------------------------------------- hranice „dneska"
/**
 * Krok UXP4a bod 5 (nalez P2-18) — JEDNA ODPOVED NA OTAZKU „KDY ZACINA DNES".
 *
 * Driv ji davaly DVE funkce, kazda jinak: `dayStartUtc` (@reos/crm) brala
 * 00:00 **UTC** a rozhodovala, co je na Dnesku, zatimco `startOfToday`
 * (@reos/today-metrics) brala 00:00 **lokalniho casu prohlizece** a vybirala
 * dnesni schuzky. V Praze se obe hranice rozchazeji kazdou noc mezi 00:00 a
 * 02:00 o CELY DEN: Dnesek pak hlasil ukoly vcerejsiho dne, kdezto Ukoly a
 * Kalendar vedle nej uz kreslily dnesni datum (nalez P2-18).
 *
 * Hranice je proto PEVNE `Europe/Prague` — kancelar, ktera ma dnesek na
 * obrazovce, pracuje v jedinem pasmu a server bezi v UTC. Stejna uvaha jako
 * u prohlidek (`web-admin/src/lib/viewing-format.ts`).
 *
 * `DEFAULT_TIMEZONE` z env se tu ZAMERNE nepouziva: tohle je cista funkce
 * volana i v prohlizeci, kde `loadEnv()` neni, a hranice dne nesmi zavisit na
 * tom, co se kde podarilo nacist.
 */
export const OFFICE_TIME_ZONE = 'Europe/Prague';
/** `YYYY-MM-DD` dne, do ktereho okamzik spada v `OFFICE_TIME_ZONE`. */
export function officeDateKey(at) {
    // `en-CA` formatuje datum presne jako `YYYY-MM-DD` (ISO), bez vlastniho vzoru.
    return new Intl.DateTimeFormat('en-CA', {
        timeZone: OFFICE_TIME_ZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).format(at);
}
/** Posun pasma proti UTC v milisekundach v danem okamziku (letni/zimni cas). */
function officeOffsetMs(at) {
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: OFFICE_TIME_ZONE,
        hourCycle: 'h23',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    }).formatToParts(at);
    const value = (type) => Number(parts.find((p) => p.type === type)?.value ?? '0');
    const asIfUtc = Date.UTC(value('year'), value('month') - 1, value('day'), value('hour'), value('minute'), value('second'));
    return asIfUtc - at.getTime();
}
/** Okamzik, kdy v `OFFICE_TIME_ZONE` zacal den obsahujici `at`. */
export function officeDayStart(at) {
    const [year, month, day] = officeDateKey(at).split('-').map(Number);
    const midnightAsIfUtc = Date.UTC(year, month - 1, day);
    // Posun se bere v odhadnutem okamziku a jednou se opravi — na prechodu
    // letniho casu se posun pred a po pulnoci lisi.
    const first = midnightAsIfUtc - officeOffsetMs(new Date(midnightAsIfUtc));
    const refined = midnightAsIfUtc - officeOffsetMs(new Date(first));
    return new Date(refined);
}
/**
 * Okamzik, kdy v `OFFICE_TIME_ZONE` zacne NASLEDUJICI den. Nepocita se jako
 * `+ 24 h` — v den prechodu letniho casu ma den 23 nebo 25 hodin.
 */
export function officeNextDayStart(at) {
    return officeDayStart(new Date(officeDayStart(at).getTime() + 36 * 3_600_000));
}
// ------------------------------------------------- interni (loopback) listener
//
// Krok MRGT1, ROZHODNUTI MRGT1-INTERNAL-SOCKET. JEDINE misto, ktere odpovida
// na otazku „prisel tenhle pozadavek z vnitrku stroje?". Driv (AS3) na ni
// odpovidaly DVE kopie `isInternalCaller` (API route + MCP gateway), obe
// stejne a obe fail-open: `x-forwarded-for` chybi => pozadavek je interni.
// Na preview to neplati — nginx proxuje na `127.0.0.1`, takze remoteAddress
// je loopback i pro provoz z internetu, a `location` bloky bez
// `proxy_set_header X-Forwarded-For` v tom vhostu uz existuji.
//
// Proto se interni provoz pozna podle SOCKETU: vlastni loopback listener na
// `ASSISTANT_INTERNAL_PORT` / `MCP_GATEWAY_INTERNAL_PORT`, ktery zadny
// verejny vhost neproxuje. `localPort` nejde podvrhnout hlavickou.
/** Hlavicky, ktere dokazuji, ze pozadavek sel pres proxy. */
const FORWARDED_HEADERS = ['x-forwarded-for', 'x-forwarded-host', 'forwarded', 'x-real-ip'];
const LOOPBACK_ADDRESSES = new Set(['127.0.0.1', '::1', '::ffff:127.0.0.1']);
/**
 * Smi tenhle pozadavek dostat odpoved z interni cesty (dnes API klic)?
 *
 * Nosna podminka je `localPort === internalPort` — tedy ze pozadavek prisel na
 * listener, ktery neni za verejnym vhostem. Loopback adresa a nepritomnost
 * forwardovacich hlavicek jsou PRITAHOVANI (muzou rozhodnuti jen zprisnit),
 * aby ani omylem nasazena proxy pred interni port nic nepropustila.
 *
 * `internalPort === undefined` (nenastaveno v prostredi) => vzdy `false`.
 */
export function isInternalListenerRequest(req, internalPort) {
    if (internalPort === undefined)
        return false;
    if (req.socket.localPort !== internalPort)
        return false;
    if (!LOOPBACK_ADDRESSES.has(req.socket.remoteAddress ?? ''))
        return false;
    return FORWARDED_HEADERS.every((header) => req.headers[header] === undefined);
}
/**
 * Otevre interni listener a jeho spojeni preda JIZ BEZICIMU HTTP serveru.
 * Druhy `net` listener (ne `http`) je zamer: kdyby to byl `http.Server`,
 * nasadily by se na jeden socket dva HTTP parsery a odpoved by se nevratila.
 * Aplikace se nestavi dvakrat — je to tentyz Fastify, jen druhy socket.
 *
 * Vraci funkci, ktera listener zavre (patri do shutdown vetve procesu).
 */
export async function openInternalListener(httpServer, port, host = '127.0.0.1') {
    const { createServer } = await import('node:net');
    const listener = createServer((socket) => {
        httpServer.emit('connection', socket);
    });
    await new Promise((resolve, reject) => {
        listener.once('error', reject);
        listener.listen(port, host, () => {
            listener.removeListener('error', reject);
            resolve();
        });
    });
    return () => new Promise((resolve) => {
        listener.close(() => resolve());
    });
}
//# sourceMappingURL=index.js.map